/**
 * Rose Farmers theme JS — header scroll, mobile nav, cart drawer (Ajax Cart API)
 */
(function () {
  "use strict";

  var cartDrawer = null;
  var cartCountEls = [];
  var moneyFormat = (window.theme && window.theme.moneyFormat) || "${{amount}}";

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /**
   * Scripted redirects to checkout. RF.beforeCheckout (AAA subdomains, assets/rf-aaa.js) may hold
   * the redirect until the shopper enters a member number; it rejects if they cancel.
   */
  function goToCheckout() {
    var gate =
      window.RF && typeof window.RF.beforeCheckout === "function"
        ? window.RF.beforeCheckout()
        : Promise.resolve();
    return Promise.resolve(gate).then(function () {
      window.location.href = "/checkout";
    });
  }

  function formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return "$" + (cents / 100).toFixed(2);
  }

  function setCartOpen(open) {
    if (!cartDrawer) return;
    cartDrawer.classList.toggle("is-open", open);
    cartDrawer.setAttribute("aria-hidden", open ? "false" : "true");
    document.body.classList.toggle("rf-cart-open", open);
  }

  function updateCounts(count) {
    cartCountEls.forEach(function (el) {
      if (count > 0) {
        el.textContent = String(count);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
  }

  function lineProperty(item, key) {
    if (!item.properties) return null;
    return item.properties[key] || null;
  }

  function isAddOn(item) {
    return !!(
      lineProperty(item, "_addon") ||
      lineProperty(item, "Add-on") ||
      lineProperty(item, "_is_addon")
    );
  }

  function groupId(item) {
    return (
      lineProperty(item, "_group_id") || lineProperty(item, "Group") || null
    );
  }

  function addonDisplayTitle(item) {
    var label = lineProperty(item, "_label");
    if (label) return label;
    var title = item.product_title || "Add-on";
    if (item.variant_title && item.variant_title !== "Default Title") {
      // Prefer a compact "Color — 12 Roses" when the Shopify title is verbose
      if (/add\s*on/i.test(title) && /^12\s+roses$/i.test(item.variant_title)) {
        return (
          title
            .replace(/\s*\(add\s*on\)\s*/i, "")
            .replace(/\s*long stem roses.*/i, "")
            .trim() + " — 12 extra stems"
        );
      }
      return title + " — " + item.variant_title;
    }
    return title;
  }

  function parentItems(cart) {
    return ((cart && cart.items) || []).filter(function (i) {
      return !isAddOn(i);
    });
  }

  /** Custom bouquet / wedding mix lines share Mix + Recipe (or _bouquet). */
  function isBouquetParent(item) {
    if (isAddOn(item)) return false;
    if (
      lineProperty(item, "_bouquet") === "custom" ||
      lineProperty(item, "_bouquet") === "wedding"
    ) {
      return true;
    }
    return !!(lineProperty(item, "Mix") && lineProperty(item, "Recipe"));
  }

  function groupAddOns(cart, gid) {
    if (!gid) return [];
    return ((cart && cart.items) || []).filter(function (i) {
      return isAddOn(i) && groupId(i) === gid;
    });
  }

  /**
   * Collapse multi-line custom bouquets into one display unit; keep other parents as lines.
   * Returns [{ type:'bouquet', gid, members, addOns }, { type:'line', item, addOns }]
   */
  function cartDisplayGroups(cart) {
    var parents = parentItems(cart);
    var seen = {};
    var groups = [];

    parents.forEach(function (item) {
      if (seen[item.key]) return;
      var gid = groupId(item);

      if (isBouquetParent(item) && gid) {
        var members = parents.filter(function (p) {
          return groupId(p) === gid && isBouquetParent(p);
        });
        members.forEach(function (m) {
          seen[m.key] = true;
        });
        groups.push({
          type: "bouquet",
          gid: gid,
          members: members,
          addOns: groupAddOns(cart, gid),
        });
        return;
      }

      seen[item.key] = true;
      groups.push({
        type: "line",
        item: item,
        addOns: addOnsForParent(cart, item),
      });
    });

    return groups;
  }

  /** One unit per bouquet group; otherwise parent quantities. Add-ons never count. */
  function displayItemCount(cart) {
    return cartDisplayGroups(cart).reduce(function (n, g) {
      if (g.type === "bouquet") return n + 1;
      return n + (g.item.quantity || 0);
    }, 0);
  }

  function firstParentKeyInGroup(cart, gid) {
    if (!gid) return null;
    var parents = parentItems(cart);
    for (var i = 0; i < parents.length; i++) {
      if (groupId(parents[i]) === gid) return parents[i].key;
    }
    return null;
  }

  function addOnsForParent(cart, item) {
    var gid = groupId(item);
    var parentKeyFallback = "parent-" + item.key;
    var isFirstInGroup = !gid || firstParentKeyInGroup(cart, gid) === item.key;
    return cart.items.filter(function (i) {
      if (!isAddOn(i)) return false;
      var ag = groupId(i);
      // Parent-specific add-ons always nest under that parent
      if (ag === parentKeyFallback) return true;
      // Shared group add-ons (e.g. Guaranteed Delivery) show once, under the first parent
      if (gid && ag === gid) return isFirstInGroup;
      return false;
    });
  }

  function bouquetGroupTitle(members) {
    var size = lineProperty(members[0], "Size");
    if (size) return "Custom Bouquet — " + size;
    return "Custom Bouquet";
  }

  function bouquetGroupMeta(members) {
    var mix = lineProperty(members[0], "Mix");
    var bunches = members.reduce(function (n, m) {
      return n + (m.quantity || 0);
    }, 0);
    var stems = bunches * 12;
    var parts = [];
    if (mix) parts.push(mix);
    parts.push(bunches + (bunches === 1 ? " bunch" : " bunches"));
    parts.push(stems + " stems");
    return parts.join(" · ");
  }

  function bouquetRecipeRows(members) {
    var recipe = lineProperty(members[0], "Recipe");
    if (recipe) {
      return recipe.split(/\s*,\s*/).filter(Boolean);
    }
    return members.map(function (m) {
      var color = lineProperty(m, "Color") || m.product_title || "Roses";
      return color + " ×" + (m.quantity || 1);
    });
  }

  function bouquetMembersPrice(members) {
    return members.reduce(function (n, m) {
      return n + (m.final_line_price || 0);
    }, 0);
  }

  var TAG_ICON =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>';

  function escapeHtml(str) {
    return String(str == null ? "" : str).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /** Line price with the pre-discount price struck through (same markup as sections/main-cart.liquid). */
  function priceHtml(original, final) {
    if (original > final) {
      return (
        '<s class="rf-price-was">' +
        formatMoney(original) +
        "</s>" +
        '<span class="rf-price-now">' +
        formatMoney(final) +
        "</span>"
      );
    }
    return formatMoney(final);
  }

  function sumLines(lines, field) {
    return lines.reduce(function (n, l) {
      return n + (l[field] || 0);
    }, 0);
  }

  /** "SYMPHONY-VIP (−$31.99)" per code applied to these lines (Ajax cart line_level_discount_allocations). */
  function lineDiscountsHtml(lines) {
    var byTitle = {};
    var order = [];
    lines.forEach(function (l) {
      (l.line_level_discount_allocations || []).forEach(function (a) {
        var title = (a.discount_application && a.discount_application.title) || "Discount";
        if (!(title in byTitle)) {
          byTitle[title] = 0;
          order.push(title);
        }
        byTitle[title] += a.amount || 0;
      });
    });
    return order
      .map(function (title) {
        return (
          '<p class="rf-cart-discount">' +
          TAG_ICON +
          "<span>" +
          escapeHtml(title) +
          " (−" +
          formatMoney(byTitle[title]) +
          ")</span></p>"
        );
      })
      .join("");
  }

  /** Footer savings row: line-level codes plus any order-level discounts. */
  function cartSavingsHtml(cart) {
    var total = cart.total_discount || 0;
    if (!total) return "";
    var titles = [];
    (cart.items || []).forEach(function (l) {
      (l.line_level_discount_allocations || []).forEach(function (a) {
        var t = a.discount_application && a.discount_application.title;
        if (t && titles.indexOf(t) === -1) titles.push(t);
      });
    });
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      if (d.title && titles.indexOf(d.title) === -1) titles.push(d.title);
    });
    return (
      '<span class="rf-cart-savings__label">' +
      TAG_ICON +
      "<span>" +
      (titles.length ? escapeHtml(titles.join(", ")) : "Discount") +
      "</span></span>" +
      '<span class="rf-cart-savings__value">−' +
      formatMoney(total) +
      "</span>"
    );
  }

  function renderAddonList(addOns) {
    if (!addOns || !addOns.length) return "";
    var html = '<ul class="rf-cart-drawer__addons">';
    addOns.forEach(function (a) {
      var title = addonDisplayTitle(a);
      html +=
        '<li class="rf-cart-drawer__addon" data-key="' +
        a.key +
        '">' +
        '<span class="rf-cart-drawer__addon-title">+ ' +
        title +
        (a.quantity > 1 ? " × " + a.quantity : "") +
        "</span>" +
        '<span class="rf-cart-drawer__addon-price">' +
        priceHtml(a.original_line_price, a.final_line_price) +
        "</span>" +
        // The card message lives on the bouquet line, so the paid card can only
        // leave the cart with its bouquet; an invisible stand-in keeps prices aligned.
        (lineProperty(a, "_addon") === "holiday-card"
          ? '<span class="rf-cart-drawer__addon-remove" aria-hidden="true" style="visibility:hidden">×</span>'
          : '<button type="button" class="rf-cart-drawer__addon-remove" data-cart-remove="' +
            a.key +
            '" aria-label="Remove ' +
            title.replace(/"/g, "") +
            '">×</button>') +
        "</li>";
    });
    html += "</ul>";
    return html;
  }

  function orphanedAddonKeys(cart) {
    var parentGids = {};
    var parentKeys = {};
    parentItems(cart).forEach(function (p) {
      parentKeys[p.key] = true;
      var g = groupId(p);
      if (g) parentGids[g] = true;
    });
    return ((cart && cart.items) || [])
      .filter(function (i) {
        if (!isAddOn(i)) return false;
        var g = groupId(i);
        if (!g) return false;
        if (g.indexOf("parent-") === 0) {
          return !parentKeys[g.slice("parent-".length)];
        }
        return !parentGids[g];
      })
      .map(function (i) {
        return i.key;
      });
  }

  function updateLines(updates) {
    return fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      return r.json();
    });
  }

  function pruneOrphanAddOns(cart) {
    var keys = orphanedAddonKeys(cart);
    if (!keys.length) return Promise.resolve(cart);
    var updates = {};
    keys.forEach(function (k) {
      updates[k] = 0;
    });
    return updateLines(updates);
  }

  /** Remove a line; if it was the last parent in a group, also clear that group's add-ons. */
  function removeCartLine(key) {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var item = (cart.items || []).find(function (i) {
          return i.key === key;
        });
        if (!item) return cart;

        var updates = {};
        updates[key] = 0;

        if (!isAddOn(item)) {
          var gid = groupId(item);
          if (gid) {
            var remaining = parentItems(cart).filter(function (p) {
              return groupId(p) === gid && p.key !== key;
            });
            if (!remaining.length) {
              (cart.items || []).forEach(function (i) {
                if (isAddOn(i) && groupId(i) === gid) updates[i.key] = 0;
              });
            }
          }
        }

        return updateLines(updates);
      });
  }

  /** Remove every line sharing a bouquet _group_id (bunches + add-ons). */
  function removeCartGroup(gid) {
    if (!gid) return Promise.resolve(null);
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var updates = {};
        (cart.items || []).forEach(function (i) {
          if (groupId(i) === gid) updates[i.key] = 0;
        });
        if (!Object.keys(updates).length) return cart;
        return updateLines(updates);
      });
  }

  function renderCart(cart) {
    if (!cartDrawer) return;
    var count = displayItemCount(cart);
    updateCounts(count);

    var body = qs("[data-cart-drawer-body]", cartDrawer);
    var footer = qs("[data-cart-drawer-footer]", cartDrawer);
    var desc = qs("[data-cart-drawer-desc]", cartDrawer);
    if (!body) return;

    if (desc) {
      desc.textContent =
        count === 0
          ? "Nothing picked yet."
          : count +
            " item" +
            (count !== 1 ? "s" : "") +
            " ready to ship from the farm.";
    }

    if (!cart.items || cart.items.length === 0 || count === 0) {
      body.innerHTML =
        '<div class="rf-cart-drawer__empty">' +
        '<span class="rf-icon-btn" aria-hidden="true">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 6h15l-1.5 9h-12z"/><circle cx="9" cy="20" r="1"/><circle cx="18" cy="20" r="1"/><path d="M6 6L5 2H2"/></svg>' +
        "</span>" +
        "<p>Your bouquet is empty. Start with our long-stem Ecuadorian roses.</p>" +
        '<a class="rf-btn rf-btn--primary" href="/collections/new-rf-sku-2026" data-cart-close>Browse the catalog</a>' +
        "</div>";
      if (footer) footer.hidden = true;
      return;
    }

    var groups = cartDisplayGroups(cart);
    var html = '<ul class="rf-cart-drawer__lines">';
    var removeIcon =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>';

    groups.forEach(function (g) {
      if (g.type === "bouquet") {
        var img =
          g.members[0].image ||
          (g.members[0].featured_image && g.members[0].featured_image.url) ||
          "";
        var rows = bouquetRecipeRows(g.members);
        html +=
          '<li class="rf-cart-drawer__line rf-cart-drawer__line--bouquet" data-group="' +
          g.gid +
          '">' +
          '<div class="rf-cart-drawer__thumb">' +
          (img ? '<img src="' + img + '" alt="">' : "") +
          "</div>" +
          '<div class="rf-cart-drawer__line-meta">' +
          '<div style="display:flex;gap:0.5rem;align-items:flex-start">' +
          '<h3 style="flex:1">' +
          bouquetGroupTitle(g.members) +
          "</h3>" +
          '<button type="button" class="rf-icon-btn" data-cart-remove-group="' +
          g.gid +
          '" aria-label="Remove custom bouquet" style="width:1.5rem;height:1.5rem">' +
          removeIcon +
          "</button></div>" +
          '<p class="rf-muted">' +
          bouquetGroupMeta(g.members) +
          "</p>" +
          lineDiscountsHtml(g.members) +
          '<ul class="rf-cart-drawer__recipe" aria-label="Bouquet recipe">';
        rows.forEach(function (row) {
          html += "<li>" + row + "</li>";
        });
        html +=
          "</ul>" +
          '<div class="rf-cart-drawer__line-foot">' +
          '<p class="rf-cart-drawer__qty-fixed">1 bouquet</p>' +
          '<p class="rf-cart-drawer__line-price">' +
          priceHtml(sumLines(g.members, "original_line_price"), bouquetMembersPrice(g.members)) +
          "</p>" +
          "</div>" +
          renderAddonList(g.addOns) +
          "</div></li>";
        return;
      }

      var item = g.item;
      var img2 =
        item.image || (item.featured_image && item.featured_image.url) || "";

      html +=
        '<li class="rf-cart-drawer__line" data-key="' +
        item.key +
        '">' +
        '<div class="rf-cart-drawer__thumb">' +
        (img2 ? '<img src="' + img2 + '" alt="">' : "") +
        "</div>" +
        '<div class="rf-cart-drawer__line-meta">' +
        '<div style="display:flex;gap:0.5rem;align-items:flex-start">' +
        '<h3 style="flex:1">' +
        item.product_title +
        "</h3>" +
        '<button type="button" class="rf-icon-btn" data-cart-remove="' +
        item.key +
        '" aria-label="Remove item" style="width:1.5rem;height:1.5rem">' +
        removeIcon +
        "</button></div>";

      if (item.variant_title && item.variant_title !== "Default Title") {
        html += '<p class="rf-muted">' + item.variant_title + "</p>";
      } else if (lineProperty(item, "Color")) {
        html += '<p class="rf-muted">' + lineProperty(item, "Color");
        if (lineProperty(item, "Mix"))
          html += " · " + lineProperty(item, "Mix");
        html += "</p>";
      } else if (lineProperty(item, "Mix")) {
        html += '<p class="rf-muted">' + lineProperty(item, "Mix") + "</p>";
      }
      html += lineDiscountsHtml([item]);

      html +=
        '<div class="rf-cart-drawer__line-foot">' +
        '<div class="rf-cart-drawer__qty">' +
        '<button type="button" data-cart-qty="' +
        item.key +
        '" data-delta="-1" aria-label="Decrease">−</button>' +
        "<span>" +
        item.quantity +
        "</span>" +
        '<button type="button" data-cart-qty="' +
        item.key +
        '" data-delta="1" aria-label="Increase">+</button>' +
        "</div>" +
        '<p class="rf-cart-drawer__line-price">' +
        priceHtml(item.original_line_price, item.final_line_price) +
        "</p>" +
        "</div>" +
        renderAddonList(g.addOns) +
        "</div></li>";
    });

    html += "</ul>";
    body.innerHTML = html;

    if (footer) {
      footer.hidden = false;
      var subtotal = qs("[data-cart-subtotal]", footer);
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
      var savings = qs("[data-cart-savings]", footer);
      if (savings) {
        savings.innerHTML = cartSavingsHtml(cart);
        savings.hidden = !cart.total_discount;
      }
    }
  }

  function isLongStemLine(item) {
    var t = (
      (item.product_title || "") +
      " " +
      (item.handle || "") +
      " " +
      (item.url || "")
    ).toLowerCase();
    if (/david austin|ranunculus|wedding|subscription|subscribe/.test(t))
      return false;
    return /long[\s-]?stem/.test(t);
  }

  var upsellModalCtx = { groupId: "", parentTitle: "" };

  function upsellModalHasOptions() {
    var modal = qs("[data-upsell-modal]");
    return !!(modal && qs("[data-upsell-option]", modal));
  }

  function closeUpsellModal(openCart) {
    var modal = qs("[data-upsell-modal]");
    if (!modal) {
      if (openCart !== false) setCartOpen(true);
      return;
    }
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    modal.classList.remove("is-open");
    document.documentElement.classList.remove("rf-upsell-open");
    qsa("[data-upsell-option]", modal).forEach(function (btn) {
      btn.classList.remove("is-selected");
      btn.setAttribute("aria-selected", "false");
    });
    var confirm = qs("[data-upsell-confirm]", modal);
    if (confirm) confirm.disabled = true;
    if (openCart !== false) setCartOpen(true);
  }

  function openUpsellModal(ctx) {
    var modal = qs("[data-upsell-modal]");
    if (!modal || !upsellModalHasOptions()) {
      setCartOpen(true);
      return;
    }
    upsellModalCtx = {
      groupId: (ctx && ctx.groupId) || "",
      parentTitle: (ctx && ctx.parentTitle) || "",
    };
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    modal.classList.add("is-open");
    document.documentElement.classList.add("rf-upsell-open");
    var confirm = qs("[data-upsell-confirm]", modal);
    if (confirm) confirm.disabled = true;
  }

  function initUpsellModal() {
    var modal = qs("[data-upsell-modal]");
    if (!modal) return;

    modal.addEventListener("click", function (e) {
      var option = e.target.closest("[data-upsell-option]");
      if (option) {
        qsa("[data-upsell-option]", modal).forEach(function (btn) {
          var on = btn === option;
          btn.classList.toggle("is-selected", on);
          btn.setAttribute("aria-selected", on ? "true" : "false");
        });
        var confirm = qs("[data-upsell-confirm]", modal);
        if (confirm) confirm.disabled = false;
        return;
      }

      if (e.target.closest("[data-upsell-dismiss]")) {
        closeUpsellModal(true);
        return;
      }

      var confirmBtn = e.target.closest("[data-upsell-confirm]");
      if (!confirmBtn || confirmBtn.disabled) return;
      var selected = qs("[data-upsell-option].is-selected", modal);
      if (!selected || !window.RF || !window.RF.addToCart) return;

      var variantId = parseInt(selected.getAttribute("data-variant-id"), 10);
      if (!variantId) return;
      var label = selected.getAttribute("data-label") || "Extra stems";
      var gid = upsellModalCtx.groupId || "rf-" + Date.now();
      var parentTitle = upsellModalCtx.parentTitle || "";

      confirmBtn.disabled = true;
      window.RF.addToCart(
        [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _group_id: gid,
              _addon: "upsell-12",
              _is_addon: "true",
              _label: label + " — 12 extra stems",
              _parent_title: parentTitle,
            },
          },
        ],
        true,
      )
        .then(function () {
          closeUpsellModal(false);
          setCartOpen(true);
        })
        .catch(function () {
          confirmBtn.disabled = false;
        });
    });

    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      if (!modal.classList.contains("is-open")) return;
      closeUpsellModal(true);
    });
  }

  /**
   * "Last chance" modal on Checkout click (cart drawer + cart page). Only for a cart holding a single
   * long-stem box of 24 roses, or 48 roses with no add-ons. To stay within the shipping box size
   * limit the shopper can add at most one 12-stem color, plus any combination of the add-ons
   * (vase, lilies, greenery) they don't have yet.
   * Shown once per session; dismissing (backdrop / Esc) cancels, "No thanks" continues to checkout.
   */
  var LAST_CHANCE_SEEN_KEY = "rf-last-chance-seen";
  // Not packed in the box, so they don't count against the 48-stem "no add-ons" rule
  var LAST_CHANCE_NON_BOX_ADDONS = ["guaranteed-delivery", "holiday-card"];

  function lastChanceSeen() {
    try {
      return !!window.sessionStorage.getItem(LAST_CHANCE_SEEN_KEY);
    } catch (e) {
      return false;
    }
  }

  function markLastChanceSeen() {
    try {
      window.sessionStorage.setItem(LAST_CHANCE_SEEN_KEY, "1");
    } catch (e) {}
  }

  function isExtraStemAddon(item) {
    var a = String(lineProperty(item, "_addon") || "");
    return a === "upsell-12" || a.indexOf("extra-stem-") === 0;
  }

  /** Returns { stems, groupId, parentTitle, addOns } or null when the cart doesn't qualify. */
  function lastChanceContext(cart) {
    var items = (cart && cart.items) || [];
    if (
      items.some(function (i) {
        return !!i.selling_plan_allocation;
      })
    )
      return null;
    var groups = cartDisplayGroups(cart);
    if (groups.length !== 1) return null;
    var g = groups[0];
    var stems, gid, parentTitle;

    if (g.type === "bouquet") {
      stems = g.members.reduce(function (n, m) {
        return n + (m.quantity || 0) * 12;
      }, 0);
      gid = g.gid;
      parentTitle = bouquetGroupTitle(g.members);
    } else {
      var item = g.item;
      if (item.quantity !== 1 || !isLongStemLine(item)) return null;
      if (/prepa(id|y)/i.test(item.product_title || "")) return null;
      stems = stemCountFromLabel(item.variant_title);
      gid = groupId(item) || "parent-" + item.key;
      parentTitle = item.product_title || "";
    }

    var boxAddOns = 0;
    g.addOns.forEach(function (a) {
      if (isExtraStemAddon(a)) {
        stems += 12 * (a.quantity || 0);
      } else if (
        LAST_CHANCE_NON_BOX_ADDONS.indexOf(lineProperty(a, "_addon")) === -1
      ) {
        boxAddOns += a.quantity || 0;
      }
    });

    if (!(stems === 24 || (stems === 48 && boxAddOns === 0))) return null;
    return {
      stems: stems,
      groupId: gid,
      parentTitle: parentTitle,
      addOns: g.addOns,
    };
  }

  function initLastChance() {
    var modal = qs("[data-last-chance-modal]");
    if (!modal) return;
    var pending = null; // { button, ctx }
    var bypass = false;

    function close() {
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      modal.classList.remove("is-open");
      document.documentElement.classList.remove("rf-upsell-open");
    }

    function proceed(btn) {
      bypass = true;
      close();
      if (btn && btn.isConnected) {
        btn.click();
      } else {
        goToCheckout().catch(function () {});
      }
      bypass = false;
    }

    /** Hide options that don't apply to this box; returns how many are left. */
    function prepareOptions(ctx) {
      var inCart = {};
      ctx.addOns.forEach(function (a) {
        inCart[String(a.variant_id)] = true;
        var h = lineProperty(a, "_addon");
        if (h) inCart["h:" + h] = true;
      });
      var visible = 0;
      qsa("[data-last-chance-option]", modal).forEach(function (opt) {
        var kind = opt.getAttribute("data-kind");
        var ok = true;
        if (kind !== "stems") {
          if (inCart[opt.getAttribute("data-variant-id")]) ok = false;
          if (inCart["h:" + opt.getAttribute("data-addon-id")]) ok = false;
          if (kind === "vase" && ctx.stems !== 24) ok = false; // vases only fit a 24-stem box
          var only = parseInt(opt.getAttribute("data-addon-stems"), 10);
          if (only && only !== ctx.stems) ok = false;
        }
        opt.hidden = !ok;
        opt.classList.remove("is-selected");
        opt.setAttribute("aria-selected", "false");
        if (ok) visible++;
      });
      qsa("[data-last-chance-group]", modal).forEach(function (grp) {
        grp.hidden = !qsa("[data-last-chance-option]", grp).some(function (o) {
          return !o.hidden;
        });
      });
      showPickPrompt(false);
      return visible;
    }

    var pickPromptTimer = null;
    function showPickPrompt(on) {
      var msg = qs("[data-last-chance-pick]", modal);
      if (!msg) return;
      if (pickPromptTimer) clearTimeout(pickPromptTimer);
      msg.hidden = !on;
      msg.classList.remove("is-flash");
      if (!on) return;
      void msg.offsetWidth; // restart the flash animation on repeat clicks
      msg.classList.add("is-flash");
      pickPromptTimer = setTimeout(function () {
        msg.hidden = true;
      }, 3500);
    }

    function open(btn, ctx) {
      pending = { button: btn, ctx: ctx };
      markLastChanceSeen();
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      modal.classList.add("is-open");
      document.documentElement.classList.add("rf-upsell-open");
    }

    document.addEventListener(
      "click",
      function (e) {
        if (bypass || lastChanceSeen()) return;
        var btn = e.target.closest('button[name="checkout"]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        btn.disabled = true;
        fetch("/cart.js", { credentials: "same-origin" })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            btn.disabled = false;
            var ctx = lastChanceContext(cart);
            if (ctx && prepareOptions(ctx) > 0) {
              open(btn, ctx);
            } else {
              markLastChanceSeen();
              proceed(btn);
            }
          })
          .catch(function () {
            btn.disabled = false;
            proceed(btn);
          });
      },
      true,
    );

    modal.addEventListener("click", function (e) {
      var option = e.target.closest("[data-last-chance-option]");
      if (option) {
        // data-pick="one" (12 stems): at most one color per box.
        // data-pick="many" (vase, lilies, greenery): toggle freely, any combination.
        var on = !option.classList.contains("is-selected");
        if (on && option.getAttribute("data-pick") === "one") {
          qsa('[data-last-chance-option][data-pick="one"]', modal).forEach(
            function (o) {
              o.classList.remove("is-selected");
              o.setAttribute("aria-selected", "false");
            },
          );
        }
        option.classList.toggle("is-selected", on);
        option.setAttribute("aria-selected", on ? "true" : "false");
        showPickPrompt(false);
        return;
      }

      if (e.target.closest("[data-last-chance-dismiss]")) {
        close();
        pending = null;
        return;
      }

      if (e.target.closest("[data-last-chance-skip]")) {
        proceed(pending && pending.button);
        pending = null;
        return;
      }

      var confirmBtn = e.target.closest("[data-last-chance-confirm]");
      if (!confirmBtn || confirmBtn.disabled || !pending) return;
      var selected = qsa("[data-last-chance-option].is-selected", modal);
      if (!selected.length) {
        showPickPrompt(true);
        return;
      }
      if (!window.RF || !window.RF.addToCart) return;

      var ctx = pending.ctx;
      var items = selected
        .map(function (opt) {
          var label = opt.getAttribute("data-label") || "";
          var props =
            opt.getAttribute("data-kind") === "stems"
              ? {
                  _group_id: ctx.groupId,
                  _addon: "upsell-12",
                  _is_addon: "true",
                  _label: label + " — 12 extra stems",
                  _parent_title: ctx.parentTitle,
                }
              : {
                  _group_id: ctx.groupId,
                  _addon: opt.getAttribute("data-addon-id") || "1",
                  _is_addon: "true",
                };
          return {
            id: parseInt(opt.getAttribute("data-variant-id"), 10),
            quantity: 1,
            properties: props,
          };
        })
        .filter(function (item) {
          return !!item.id;
        });
      if (!items.length) return;

      confirmBtn.disabled = true;
      window.RF.addToCart(items, false)
        .then(goToCheckout)
        .catch(function () {
          confirmBtn.disabled = false;
        });
    });

    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" || !modal.classList.contains("is-open")) return;
      close();
      pending = null;
    });
  }

  function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        return pruneOrphanAddOns(cart);
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function changeLine(key, quantity) {
    if (quantity === 0) return removeCartLine(key);
    return fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (r) {
      return r.json();
    });
  }

  function initHeader() {
    var header = qs("[data-rf-header]");
    if (header) {
      var onScroll = function () {
        header.classList.toggle("is-scrolled", window.scrollY > 8);
      };
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    var toggle = qs("[data-mobile-nav-toggle]");
    var mobileNav = qs("[data-mobile-nav]");
    if (toggle && mobileNav) {
      toggle.addEventListener("click", function () {
        var open = !mobileNav.classList.contains("is-open");
        mobileNav.classList.toggle("is-open", open);
        toggle.setAttribute("aria-expanded", open ? "true" : "false");
      });
    }
  }

  function initAnnouncement() {
    var bar = qs("[data-rf-announcement]");
    if (!bar) return;

    // Visibility is decided by an inline script in announcement-bar.liquid
    // (start hidden → reveal if not dismissed) to avoid a post-load flicker.
    var TTL_MS = 24 * 60 * 60 * 1000;
    var messageEl = qs(".rf-announcement__message", bar);
    var message = ((messageEl && messageEl.textContent) || "")
      .replace(/\s+/g, " ")
      .trim();
    var storageKey = "rf-announcement-dismissed:" + message;

    var dismiss = qs("[data-announcement-dismiss]", bar);
    if (!dismiss) return;
    dismiss.addEventListener("click", function () {
      bar.hidden = true;
      try {
        if (window.localStorage) {
          localStorage.setItem(
            storageKey,
            JSON.stringify({ until: Date.now() + TTL_MS }),
          );
        }
      } catch (e) {}
    });
  }

  function initCartDrawer() {
    cartDrawer = qs("[data-cart-drawer]");
    cartCountEls = qsa("[data-cart-count]");
    if (!cartDrawer) return;

    qsa("[data-cart-open]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        setCartOpen(true);
        fetchCart();
      });
    });

    qsa("[data-cart-close]", cartDrawer).forEach(function (el) {
      el.addEventListener("click", function () {
        setCartOpen(false);
      });
    });

    var overlay = qs("[data-cart-overlay]", cartDrawer);
    if (overlay) {
      overlay.addEventListener("click", function () {
        setCartOpen(false);
      });
    }

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") setCartOpen(false);
    });

    cartDrawer.addEventListener("click", function (e) {
      var closeBtn = e.target.closest("[data-cart-close]");
      if (closeBtn) {
        setCartOpen(false);
        return;
      }

      var removeGroup = e.target.closest("[data-cart-remove-group]");
      if (removeGroup) {
        removeCartGroup(
          removeGroup.getAttribute("data-cart-remove-group"),
        ).then(renderCart);
        return;
      }

      var remove = e.target.closest("[data-cart-remove]");
      if (remove) {
        removeCartLine(remove.getAttribute("data-cart-remove")).then(
          renderCart,
        );
        return;
      }

      var qtyBtn = e.target.closest("[data-cart-qty]");
      if (qtyBtn) {
        var key = qtyBtn.getAttribute("data-cart-qty");
        var delta = parseInt(qtyBtn.getAttribute("data-delta"), 10) || 0;
        var line = qtyBtn.closest("[data-key]");
        var current = line
          ? parseInt(qs(".rf-cart-drawer__qty span", line).textContent, 10)
          : 1;
        var next = Math.max(0, current + delta);
        (next === 0 ? removeCartLine(key) : changeLine(key, next)).then(
          renderCart,
        );
      }
    });

    fetchCart();

    document.addEventListener("rf:cart:refresh", function () {
      fetchCart();
    });

    document.addEventListener("rf:cart:open", function () {
      setCartOpen(true);
      fetchCart();
    });
  }

  function initCartPage() {
    var page = qs("[data-cart-page]");
    if (!page) return;

    // Clear orphaned group add-ons (e.g. Guaranteed Delivery left after all bunches removed)
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var keys = orphanedAddonKeys(cart);
        if (!keys.length) return null;
        var updates = {};
        keys.forEach(function (k) {
          updates[k] = 0;
        });
        return updateLines(updates).then(function () {
          window.location.reload();
        });
      })
      .catch(function () {});

    var qtyDebounce = {};
    var qtyInFlight = {};
    var qtyQueued = {};

    function applyCartPageFromCart(cart) {
      var count = displayItemCount(cart);
      updateCounts(count);

      var desc = qs("[data-cart-page-desc]", page);
      if (desc) {
        desc.textContent =
          count === 0
            ? "Nothing picked yet."
            : count +
              " item" +
              (count !== 1 ? "s" : "") +
              " ready to ship from the farm.";
      }

      var subtotal = qs("[data-cart-page-subtotal]", page);
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
      var pageSavings = qs("[data-cart-savings]", page);
      if (pageSavings) {
        pageSavings.innerHTML = cartSavingsHtml(cart);
        pageSavings.hidden = !cart.total_discount;
      }

      (cart.items || []).forEach(function (item) {
        var priceEl = page.querySelector(
          '[data-cart-page-line-price="' + item.key + '"]',
        );
        if (priceEl)
          priceEl.innerHTML = priceHtml(item.original_line_price, item.final_line_price);
        var input = page.querySelector(
          '[data-cart-page-qty="' + item.key + '"]',
        );
        if (input && !qtyInFlight[item.key] && qtyQueued[item.key] == null) {
          input.value = String(item.quantity);
          input.setAttribute("data-last-qty", String(item.quantity));
        }
      });

      if (cartDrawer) renderCart(cart);

      if (count === 0) {
        window.location.reload();
      }
    }

    function commitCartPageQty(input) {
      var key = input.getAttribute("data-cart-page-qty");
      if (!key) return;
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      var last = parseInt(input.getAttribute("data-last-qty"), 10);
      if (!isNaN(last) && qty === last && qtyQueued[key] == null) return;

      if (qtyInFlight[key]) {
        qtyQueued[key] = qty;
        return;
      }

      qtyInFlight[key] = true;
      input.setAttribute("data-last-qty", String(qty));
      var req = qty === 0 ? removeCartLine(key) : changeLine(key, qty);
      req
        .then(function (cart) {
          if (qty === 0) {
            window.location.reload();
            return;
          }
          applyCartPageFromCart(cart);
        })
        .catch(function () {
          input.setAttribute(
            "data-last-qty",
            isNaN(last) ? input.value : String(last),
          );
          if (!isNaN(last)) input.value = String(last);
        })
        .finally(function () {
          delete qtyInFlight[key];
          if (qtyQueued[key] != null) {
            var next = qtyQueued[key];
            delete qtyQueued[key];
            input.value = String(next);
            commitCartPageQty(input);
          }
        });
    }

    function scheduleCartPageQty(input) {
      var key = input.getAttribute("data-cart-page-qty");
      if (!key) return;
      if (qtyDebounce[key]) clearTimeout(qtyDebounce[key]);
      qtyDebounce[key] = setTimeout(function () {
        delete qtyDebounce[key];
        commitCartPageQty(input);
      }, 400);
    }

    function flushCartPageQty(input) {
      var key = input.getAttribute("data-cart-page-qty");
      if (!key) return;
      if (qtyDebounce[key]) {
        clearTimeout(qtyDebounce[key]);
        delete qtyDebounce[key];
      }
      commitCartPageQty(input);
    }

    page.addEventListener("input", function (e) {
      var input = e.target.closest("[data-cart-page-qty]");
      if (!input) return;
      scheduleCartPageQty(input);
    });

    page.addEventListener("change", function (e) {
      var input = e.target.closest("[data-cart-page-qty]");
      if (!input) return;
      flushCartPageQty(input);
    });

    page.addEventListener("keydown", function (e) {
      if (e.key !== "Enter") return;
      var input = e.target.closest("[data-cart-page-qty]");
      if (!input) return;
      e.preventDefault();
      flushCartPageQty(input);
      input.blur();
    });

    page.addEventListener("click", function (e) {
      var groupBtn = e.target.closest("[data-cart-page-remove-group]");
      if (groupBtn) {
        e.preventDefault();
        var gid = groupBtn.getAttribute("data-cart-page-remove-group");
        if (!gid) return;
        removeCartGroup(gid).then(function () {
          window.location.reload();
        });
        return;
      }

      var btn = e.target.closest("[data-cart-page-remove]");
      if (!btn) return;
      e.preventDefault();
      var key = btn.getAttribute("data-cart-page-remove");
      if (!key) return;
      removeCartLine(key).then(function () {
        window.location.reload();
      });
    });
  }

  /**
   * Multi-line ATC helper for bouquet + add-ons.
   * items: [{ id, quantity, properties }]
   */
  window.RF = window.RF || {};
  window.RF.addToCart = function (items, openDrawer) {
    return fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items: items }),
    })
      .then(function (r) {
        if (!r.ok)
          return r.json().then(function (err) {
            throw err;
          });
        return r.json();
      })
      .then(function () {
        return fetchCart();
      })
      .then(function (cart) {
        if (openDrawer !== false) setCartOpen(true);
        return cart;
      });
  };

  document.addEventListener("DOMContentLoaded", function () {
    initHeader();
    initAnnouncement();
    initCartDrawer();
    initCartPage();
    initUpsellModal();
    initLastChance();
    initWelcomePopup();
    initFeelings();
    initInstagram();
    initInstagramApp();
    initHero();
    initProduct();
    initBuilders();
    initAboutSlideshow();
    initDeliveryDatePickers();
    fetchCart().catch(function () {});
  });

  function moneyFromCents(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return "$" + (cents / 100).toFixed(2);
  }

  /**
   * Add-on tiles locked to one stem count (data-addon-stems, e.g. lilies = 24
   * only): shown only while that size is selected, and deselected when hidden
   * so they can't reach the cart on any other size.
   */
  function syncStemLockedAddons(root, stems) {
    stems = parseInt(stems, 10) || 0;
    qsa("[data-addon-stems]", root).forEach(function (el) {
      var ok = parseInt(el.getAttribute("data-addon-stems"), 10) === stems;
      el.hidden = !ok;
      if (!ok) el.classList.remove("is-active");
    });
  }

  /**
   * Size gates for PDP + custom bouquet:
   * - 48+ stems: hide Select a Vase
   * - 96+ stems: hide Select a Vase and Complete Your Bouquet (greenery)
   * - stem-locked add-ons (lilies): see syncStemLockedAddons
   */
  function syncSizeGatedAddons(root, stems) {
    if (!root) return;
    stems = parseInt(stems, 10) || 0;
    syncStemLockedAddons(root, stems);
    var vaseOk = stems > 0 && stems < 48;
    var greeneryOk = stems > 0 && stems < 96;

    var vaseSection = qs("[data-vase-section]", root);
    var vaseGroup = qs("[data-vase-group]", root);
    var vaseNote = qs("[data-vase-note]", root);
    var vaseBlocked = qs("[data-vase-blocked]", root);
    var greeneryGroup = qs("[data-greenery-group]", root);
    var greeneryBlocked = qs("[data-greenery-blocked]", root);

    if (vaseSection) {
      vaseSection.hidden = !vaseOk;
    } else if (vaseGroup) {
      vaseGroup.hidden = !vaseOk;
    }
    if (vaseNote && !vaseSection) vaseNote.hidden = !vaseOk;
    if (vaseBlocked) vaseBlocked.hidden = vaseOk || stems <= 0;

    if (greeneryGroup) greeneryGroup.hidden = !greeneryOk;
    if (greeneryBlocked) greeneryBlocked.hidden = greeneryOk || stems <= 0;

    if (!vaseOk) {
      qsa('[data-addon-kind="vase"]', root).forEach(function (b) {
        b.classList.remove("is-active");
      });
    }
    if (!greeneryOk) {
      qsa('[data-addon-kind="greenery"]', root).forEach(function (b) {
        b.classList.remove("is-active");
      });
    }
  }

  function stemCountFromLabel(label) {
    var m = String(label || "").match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  }

  function selectedAddOnsIn(root) {
    var items = [];
    qsa("[data-addon]", root).forEach(function (el) {
      var active = el.matches("button")
        ? el.classList.contains("is-active")
        : el.checked;
      if (!active || el.hidden) return;
      items.push({
        id: parseInt(el.getAttribute("data-variant-id"), 10),
        quantity: 1,
        price: parseInt(el.getAttribute("data-price"), 10) || 0,
        addonId: el.getAttribute("data-addon-id"),
      });
    });
    return items;
  }

  // The card message rides on the paid holiday-card add-on.  Unticking the card
  // blanks the message + sender so a note can't reach the cart without the card.
  function toggleCardFields(root, checked) {
    var cardFields = qs("[data-card-fields]", root);
    if (!cardFields) return;
    cardFields.hidden = !checked;
    if (!checked) {
      qsa("[data-card-message], [data-card-from]", cardFields).forEach(
        function (field) {
          field.value = "";
        },
      );
    }
  }

  function bindAddonClicks(root, onChange) {
    qsa("[data-addon]", root).forEach(function (el) {
      el.addEventListener("click", function (e) {
        if (!el.matches("button")) return;
        e.preventDefault();
        var kind = el.getAttribute("data-addon-kind");
        if (kind === "vase") {
          var was = el.classList.contains("is-active");
          qsa('[data-addon-kind="vase"]', root).forEach(function (b) {
            b.classList.remove("is-active");
          });
          if (!was) el.classList.add("is-active");
        } else {
          el.classList.toggle("is-active");
        }
        if (onChange) onChange();
      });
      el.addEventListener("change", function () {
        if (el.getAttribute("data-toggles-card") != null) {
          toggleCardFields(root, el.checked);
        }
        var label = el.closest(".rf-addon");
        if (label) label.classList.toggle("is-active", el.checked);
        if (onChange) onChange();
      });
    });
  }

  function familyFor(handle) {
    var h = (handle || "").toLowerCase();
    if (h.indexOf("red") >= 0) return "red";
    if (h.indexOf("pink") >= 0) return "pink";
    if (h.indexOf("orange") >= 0 || h.indexOf("peach") >= 0) return "warm";
    if (h.indexOf("yellow") >= 0) return "yellow";
    if (h.indexOf("purple") >= 0 || h.indexOf("lavender") >= 0) return "cool";
    if (h.indexOf("white") >= 0 || h.indexOf("cream") >= 0) return "neutral";
    return "neutral";
  }

  function paletteName(shades) {
    if (!shades.length)
      return { name: "Untitled", blurb: "a recipe waiting to be written" };
    var tally = {};
    shades.forEach(function (s) {
      var f = familyFor(s.handle);
      tally[f] = (tally[f] || 0) + s.qty;
    });
    var families = Object.keys(tally).sort(function (a, b) {
      return tally[b] - tally[a];
    });
    if (families.length >= 4)
      return { name: "Painterly", blurb: "a full spectrum, farm-mixed" };
    if (families.length === 1) {
      var map = {
        red: ["Crimson Vow", "deep and romantic"],
        pink: ["Blush Chorus", "soft and celebratory"],
        warm: ["Sunset Grove", "peach-to-citrus warmth"],
        yellow: ["Golden Hour", "bright and sunny"],
        cool: ["Lavender Drift", "cool and quiet"],
        neutral: ["Ivory Quiet", "clean and editorial"],
      };
      var hit = map[families[0]] || [
        "Untitled",
        "a recipe waiting to be written",
      ];
      return { name: hit[0], blurb: hit[1] };
    }
    return {
      name: "Layered Aura",
      blurb: "a composed mix of complementary shades",
    };
  }

  function initColorGrid(root, opts) {
    var selections = {};
    var max = opts.maxBunches;

    function total() {
      return Object.keys(selections).reduce(function (s, k) {
        return s + selections[k];
      }, 0);
    }

    function activeShades() {
      return qsa("[data-color-tile]", root)
        .map(function (tile) {
          var handle = tile.getAttribute("data-handle");
          var qty = selections[handle] || 0;
          if (!qty) return null;
          return {
            handle: handle,
            qty: qty,
            hex: tile.getAttribute("data-hex") || "#ccc",
            title:
              (tile.getAttribute("data-title") || handle)
                .replace(/long stem.*/i, "")
                .trim() || handle,
            weddingVariantId: tile.getAttribute("data-wedding-variant-id"),
            bunchVariantId: tile.getAttribute("data-bunch-variant-id"),
            bunchPrice:
              parseInt(tile.getAttribute("data-bunch-price"), 10) || 0,
            el: tile,
          };
        })
        .filter(Boolean);
    }

    function render() {
      var t = total();
      qsa("[data-color-tile]", root).forEach(function (tile) {
        var handle = tile.getAttribute("data-handle");
        var qty = selections[handle] || 0;
        tile.classList.toggle("is-active", qty > 0);
        var badge = qs("[data-tile-badge]", tile);
        var qtyEl = qs("[data-tile-qty]", tile);
        var minus = qs("[data-tile-minus]", tile);
        var plus = qs("[data-tile-plus]", tile);
        if (badge) {
          badge.hidden = qty <= 0;
          badge.textContent = "×" + qty;
        }
        if (qtyEl)
          qtyEl.textContent = qty + (qty === 1 ? " bunch" : " bunches");
        if (minus) minus.disabled = qty <= 0;
        if (plus) plus.disabled = t >= max;
      });
      if (opts.onChange)
        opts.onChange({
          total: t,
          remaining: max - t,
          complete: t === max,
          shades: activeShades(),
        });
    }

    qsa("[data-color-tile]", root).forEach(function (tile) {
      var handle = tile.getAttribute("data-handle");
      qs("[data-tile-minus]", tile).addEventListener("click", function () {
        selections[handle] = Math.max(0, (selections[handle] || 0) - 1);
        if (!selections[handle]) delete selections[handle];
        render();
      });
      qs("[data-tile-plus]", tile).addEventListener("click", function () {
        if (total() >= max) return;
        selections[handle] = (selections[handle] || 0) + 1;
        render();
      });
    });

    render();
    return {
      reset: function () {
        selections = {};
        render();
      },
      setMax: function (n) {
        max = n;
        Object.keys(selections).forEach(function (k) {
          if (selections[k] > max) selections[k] = max;
        });
        // clamp total
        while (total() > max) {
          var keys = Object.keys(selections);
          if (!keys.length) break;
          var last = keys[keys.length - 1];
          selections[last] -= 1;
          if (!selections[last]) delete selections[last];
        }
        render();
      },
      getState: function () {
        return {
          total: total(),
          remaining: max - total(),
          complete: total() === max,
          shades: activeShades(),
        };
      },
    };
  }

  function initCustomBuilder(root) {
    var variants = {};
    try {
      variants = JSON.parse(qs("[data-custom-variants]", root).textContent);
    } catch (e) {}
    var pricePerBunch =
      parseInt(root.getAttribute("data-price-per-bunch"), 10) || 3999;
    var sizeBtn =
      qs("[data-size-grid] .is-active", root) ||
      qs("[data-size-grid] [data-size]", root);
    var grid;

    function sizeState() {
      return {
        id: sizeBtn.getAttribute("data-size"),
        bunches: parseInt(sizeBtn.getAttribute("data-bunches"), 10),
        stems: parseInt(sizeBtn.getAttribute("data-stems"), 10),
        name: sizeBtn.getAttribute("data-name"),
      };
    }

    function updateSizeGatedAddons() {
      syncSizeGatedAddons(root, sizeState().stems);
    }

    function refresh(state) {
      var size = sizeState();
      var sel = qs("[data-bunch-selected]", root);
      var maxEl = qs("[data-bunch-max]", root);
      var target = qs("[data-bunch-target]", root);
      if (sel) sel.textContent = String(state.total);
      if (maxEl) maxEl.textContent = String(size.bunches);
      if (target) target.textContent = String(size.bunches);

      var mix = paletteName(state.shades);
      var mixName = qs("[data-mix-name]", root);
      var mixBlurb = qs("[data-mix-blurb]", root);
      var recipeNo = qs("[data-recipe-no]", root);
      var recipeDate = qs("[data-recipe-date]", root);
      var bar = qs("[data-recipe-bar]", root);
      var list = qs("[data-recipe-list]", root);
      var foot = qs("[data-recipe-foot]", root);
      if (mixName) mixName.textContent = mix.name;
      if (mixBlurb) mixBlurb.textContent = mix.blurb;
      if (recipeNo)
        recipeNo.textContent = String(state.shades.length).padStart(2, "0");
      if (recipeDate) {
        recipeDate.textContent = new Date()
          .toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          .toUpperCase();
      }
      if (bar) {
        if (!state.shades.length) {
          bar.innerHTML =
            '<div style="width:100%;background:var(--rf-border);opacity:.5"></div>';
        } else {
          bar.innerHTML = state.shades
            .map(function (s) {
              return (
                '<div style="background:' +
                s.hex +
                ";width:" +
                (s.qty / state.total) * 100 +
                '%"></div>'
              );
            })
            .join("");
        }
      }
      if (list) {
        if (!state.shades.length) {
          list.innerHTML =
            '<li class="rf-recipe-card__empty">Begin your recipe — choose a shade above.</li>';
        } else {
          list.innerHTML = state.shades
            .map(function (s) {
              var pct = Math.round((s.qty / state.total) * 100);
              return (
                '<li><span class="rf-recipe-card__swatch" style="background:' +
                s.hex +
                '"></span><span>' +
                s.title +
                '</span><span class="rf-recipe-card__meta-right">' +
                s.qty +
                (s.qty === 1 ? " bunch" : " bunches") +
                " · " +
                s.qty * 12 +
                " stems · " +
                pct +
                "%</span></li>"
              );
            })
            .join("");
        }
      }
      if (foot) {
        foot.textContent =
          state.total +
          " / " +
          size.bunches +
          " bunches · " +
          state.total * 12 +
          " stems · " +
          size.name;
      }

      var addOns = selectedAddOnsIn(root);
      var addOnCents =
        state.total > 0
          ? addOns.reduce(function (s, a) {
              return s + a.price;
            }, 0)
          : 0;
      var bunchCents = state.shades.reduce(function (sum, s) {
        var unit = s.bunchPrice || pricePerBunch;
        return sum + unit * s.qty;
      }, 0);
      var estimate = bunchCents + addOnCents;
      var status = qs("[data-sticky-status]", root);
      var price = qs("[data-sticky-price]", root);
      var atcButtons = qsa("[data-builder-atc]", root);
      var checkoutButtons = qsa("[data-builder-checkout]", root);
      var deliveryEl = qs("[data-delivery-date]", root);
      var hasDelivery = !!(
        deliveryEl &&
        deliveryEl.getAttribute("data-date-picked") === "1" &&
        (deliveryEl.getAttribute("data-date") || deliveryEl.value)
      );
      if (status) {
        if (!state.complete) {
          status.textContent =
            state.remaining +
            (state.remaining === 1 ? " bunch left" : " bunches left");
        } else if (!hasDelivery) {
          status.textContent = "Choose a delivery date";
        } else {
          status.textContent = size.name + " · ready";
        }
      }
      if (price)
        price.textContent = state.total ? moneyFromCents(estimate) : "—";
      atcButtons.forEach(function (atc) {
        atc.disabled = !state.complete;
        if (!state.complete) {
          atc.innerHTML = "Finish your recipe <span aria-hidden>→</span>";
        } else if (!hasDelivery) {
          atc.innerHTML = "Choose a delivery date <span aria-hidden>→</span>";
        } else {
          atc.innerHTML = "Add bouquet to cart <span aria-hidden>→</span>";
        }
      });
      checkoutButtons.forEach(function (btn) {
        btn.disabled = !state.complete;
      });
    }

    grid = initColorGrid(root, {
      maxBunches: sizeState().bunches,
      onChange: refresh,
    });

    var deliveryInput = qs("[data-delivery-date]", root);
    if (deliveryInput) {
      deliveryInput.addEventListener("change", function () {
        refresh(grid.getState());
      });
    }

    qsa("[data-size-grid] [data-size]", root).forEach(function (btn) {
      btn.addEventListener("click", function () {
        qsa("[data-size-grid] [data-size]", root).forEach(function (b) {
          b.classList.toggle("is-active", b === btn);
          b.setAttribute("aria-pressed", b === btn ? "true" : "false");
        });
        sizeBtn = btn;
        grid.reset();
        grid.setMax(sizeState().bunches);
        updateSizeGatedAddons();
        qs("[data-sticky-added]", root).hidden = true;
      });
    });

    bindAddonClicks(root, function () {
      refresh(grid.getState());
    });
    updateSizeGatedAddons();

    // quietMissingDate: the sticky button already reads "Choose a delivery
    // date", so clicking it just takes the customer to the picker, no alert.
    function addBouquetToCart(openDrawer, quietMissingDate) {
      var state = grid.getState();
      if (!state.complete) return Promise.reject(new Error("incomplete"));
      var deliveryEl = qs("[data-delivery-date]", root);
      var delivery =
        (deliveryEl &&
          deliveryEl.getAttribute("data-date-picked") === "1" &&
          (deliveryEl.getAttribute("data-date") || deliveryEl.value)) ||
        "";
      if (!delivery) {
        if (deliveryEl) {
          deliveryEl.focus();
          deliveryEl.click();
        }
        if (!quietMissingDate) alert("Please choose a delivery date.");
        return Promise.reject(new Error("delivery-required"));
      }
      var size = sizeState();
      var mix = paletteName(state.shades);
      var recipe = state.shades
        .map(function (s) {
          return s.title + " ×" + s.qty;
        })
        .join(", ");
      var palette = state.shades
        .map(function (s) {
          return s.hex + ":" + s.qty;
        })
        .join(",");
      var groupId = "rf-" + Date.now();
      var props = {
        _group_id: groupId,
        _bouquet: "custom",
        Mix: mix.name,
        Recipe: recipe,
        Palette: palette,
        Size: size.name,
        "Delivery Date": delivery,
      };
      var cardFrom = (qs("[data-card-from]", root) || {}).value || "";
      var cardMessage = (qs("[data-card-message]", root) || {}).value || "";
      var holidayOn = selectedAddOnsIn(root).some(function (a) {
        return a.addonId === "holiday-card";
      });
      if (holidayOn && !String(cardMessage || "").trim()) {
        openCardPrompt(root);
        return Promise.reject(new Error("card-required"));
      }
      // No paid card, no note -- even if a restored form still holds text.
      if (holidayOn) {
        if (cardFrom) props.Sender = cardFrom;
        if (cardMessage) props["Card Message"] = cardMessage;
      }

      var parentVariant = variants[size.name];
      var useParent =
        root.getAttribute("data-atc-mode") === "parent" &&
        parentVariant &&
        parentVariant.id;
      var items = [];

      if (useParent) {
        items.push({ id: parentVariant.id, quantity: 1, properties: props });
      } else {
        var missing = state.shades.filter(function (s) {
          return !s.bunchVariantId;
        });
        if (missing.length) {
          alert(
            "Some colors are missing Custom Bouquet add-on variants in Shopify (" +
              missing
                .map(function (s) {
                  return s.title;
                })
                .join(", ") +
              ").",
          );
          return Promise.reject(new Error("missing-bunch-variant"));
        }
        state.shades.forEach(function (s) {
          items.push({
            id: parseInt(s.bunchVariantId, 10),
            quantity: s.qty,
            properties: Object.assign({}, props, {
              Color: s.title,
              Bunches: String(s.qty),
            }),
          });
        });
      }

      items = items.concat(
        selectedAddOnsIn(root).map(function (a) {
          return {
            id: a.id,
            quantity: 1,
            properties: {
              _group_id: groupId,
              _addon: a.addonId || "1",
              _is_addon: "true",
            },
          };
        }),
      );

      return window.RF.addToCart(items, openDrawer !== false).then(
        function (cart) {
          var added = qs("[data-sticky-added]", root);
          if (added) added.hidden = false;
          return cart;
        },
      );
    }

    qsa("[data-builder-atc]", root).forEach(function (btn) {
      btn.addEventListener("click", function () {
        qsa("[data-builder-atc]", root).forEach(function (b) {
          b.disabled = true;
        });
        addBouquetToCart(true, true)
          .catch(function () {})
          .finally(function () {
            refresh(grid.getState());
          });
      });
    });

    qsa("[data-builder-checkout]", root).forEach(function (btn) {
      btn.addEventListener("click", function () {
        qsa("[data-builder-checkout], [data-builder-atc]", root).forEach(
          function (b) {
            b.disabled = true;
          },
        );
        addBouquetToCart(false)
          .then(goToCheckout)
          .catch(function () {
            refresh(grid.getState());
          });
      });
    });
  }

  function initWeddingsBuilder(root) {
    var bunches = parseInt(root.getAttribute("data-bunches"), 10) || 7;
    var stemsPer =
      parseInt(root.getAttribute("data-stems-per-bunch"), 10) || 24;
    var totalStems =
      parseInt(root.getAttribute("data-total-stems"), 10) || bunches * stemsPer;

    function deliveryPicked() {
      var el = qs("[data-delivery-date]", root);
      return !!(
        el &&
        el.getAttribute("data-date-picked") === "1" &&
        (el.getAttribute("data-date") || el.value)
      );
    }

    function refresh(state) {
      var sel = qs("[data-bunch-selected]", root);
      var meta = qs("[data-bundle-meta]", root);
      var bar = qs("[data-recipe-bar]", root);
      var list = qs("[data-recipe-list]", root);
      var status = qs("[data-sticky-status]", root);
      var atc = qs("[data-builder-atc]", root);
      var hasDelivery = deliveryPicked();
      if (sel) sel.textContent = String(state.total);
      if (meta)
        meta.textContent =
          state.total +
          " / " +
          bunches +
          " bunches · " +
          state.total * stemsPer +
          " stems";
      if (bar) {
        if (!state.shades.length) {
          bar.innerHTML =
            '<div style="width:100%;background:var(--rf-border);opacity:.5"></div>';
        } else {
          bar.innerHTML = state.shades
            .map(function (s) {
              return (
                '<div style="background:' +
                s.hex +
                ";width:" +
                (s.qty / state.total) * 100 +
                '%"></div>'
              );
            })
            .join("");
        }
      }
      if (list) {
        if (!state.shades.length) {
          list.innerHTML =
            '<li class="rf-recipe-card__empty">Choose your first color above to start the bundle.</li>';
        } else {
          list.innerHTML = state.shades
            .map(function (s) {
              return (
                '<li><span class="rf-recipe-card__swatch" style="background:' +
                s.hex +
                '"></span><span>' +
                s.title +
                '</span><span class="rf-recipe-card__meta-right">' +
                s.qty +
                (s.qty === 1 ? " bunch" : " bunches") +
                " · " +
                s.qty * stemsPer +
                " stems</span></li>"
              );
            })
            .join("");
        }
      }
      if (status) {
        if (!state.complete) {
          status.textContent =
            state.remaining +
            (state.remaining === 1 ? " bunch left" : " bunches left");
        } else if (!hasDelivery) {
          status.textContent = "Choose a delivery date";
        } else {
          status.textContent = "Wedding special · " + totalStems + " roses";
        }
      }
      if (atc) {
        atc.disabled = !state.complete;
        if (!state.complete) {
          atc.innerHTML = "Pick your bunches <span aria-hidden>→</span>";
        } else if (!hasDelivery) {
          atc.innerHTML = "Choose a delivery date <span aria-hidden>→</span>";
        } else {
          atc.innerHTML = "Add to cart <span aria-hidden>→</span>";
        }
      }
    }

    var grid = initColorGrid(root, { maxBunches: bunches, onChange: refresh });

    var deliveryEl = qs("[data-delivery-date]", root);
    if (deliveryEl) {
      deliveryEl.addEventListener("change", function () {
        refresh(grid.getState());
      });
    }

    qs("[data-builder-atc]", root).addEventListener("click", function () {
      var state = grid.getState();
      if (!state.complete) return;
      if (!deliveryPicked()) {
        if (deliveryEl) {
          deliveryEl.focus();
          deliveryEl.click();
        }
        return;
      }
      var delivery =
        (deliveryEl &&
          (deliveryEl.getAttribute("data-date") || deliveryEl.value)) ||
        "";
      var groupId = "rf-" + Date.now();
      var recipe = state.shades
        .map(function (s) {
          return s.title + " ×" + s.qty;
        })
        .join(", ");
      var palette = state.shades
        .map(function (s) {
          return s.hex + ":" + s.qty;
        })
        .join(",");
      var withBundle = state.shades.filter(function (s) {
        return s.weddingVariantId;
      });
      var items;

      if (withBundle.length === state.shades.length) {
        items = state.shades.map(function (s) {
          return {
            id: parseInt(s.weddingVariantId, 10),
            quantity: s.qty,
            properties: {
              _group_id: groupId,
              Bundle: "Your Event " + totalStems,
              "Delivery Date": delivery,
            },
          };
        });
      } else {
        // Production fallback: single Wedding Package SKU ($268) + recipe properties
        // until per-color "Wedding Bundle — 24 Roses" variants exist.
        var packageId = parseInt(
          root.getAttribute("data-package-variant-id"),
          10,
        );
        if (!packageId) {
          alert("Wedding Package product is missing in Shopify.");
          return;
        }
        items = [
          {
            id: packageId,
            quantity: 1,
            properties: {
              _group_id: groupId,
              Bundle: "Your Event " + totalStems,
              Recipe: recipe,
              Palette: palette,
              "Delivery Date": delivery,
            },
          },
        ];
      }

      var btn = qs("[data-builder-atc]", root);
      btn.disabled = true;
      window.RF.addToCart(items, true)
        .then(function () {
          qs("[data-sticky-added]", root).hidden = false;
        })
        .catch(function () {})
        .finally(function () {
          btn.disabled = false;
          refresh(grid.getState());
        });
    });
  }

  function initSubscriptionsBuilder(root) {
    var catalog = {};
    try {
      catalog = JSON.parse(qs("[data-sub-catalog]", root).textContent);
    } catch (e) {}

    var colorNotes = {
      farm: "our grower's choice",
      red: "Red",
      lavender: "Lavender",
      white: "White",
      pink: "Pink",
    };
    var cadenceLabels = {
      weekly: "Weekly",
      biweekly: "Every 2 Weeks",
      monthly: "Every Month",
    };

    function colorCadenceNote(s) {
      var color = colorNotes[s.color] || s.colorLabel || "roses";
      if (s.cadence === "monthly")
        return "You will receive " + color + " each month";
      if (s.cadence === "weekly")
        return "You will receive " + color + " every week";
      return "You will receive " + color + " every two weeks";
    }

    function state() {
      var colorBtn =
        qs("[data-sub-colors] .is-active", root) ||
        qs("[data-sub-colors] [data-color]", root);
      var stemsBtn =
        qs("[data-sub-bundles] .is-active", root) ||
        qs("[data-sub-bundles] [data-stems]", root);
      var cadenceBtn =
        qs("[data-sub-cadence] .is-active", root) ||
        qs("[data-sub-cadence] [data-cadence]", root);
      return {
        color: colorBtn.getAttribute("data-color"),
        colorLabel: colorBtn.getAttribute("data-label"),
        stems: stemsBtn.getAttribute("data-stems"),
        price: parseInt(stemsBtn.getAttribute("data-price"), 10) || 0,
        cadence: cadenceBtn.getAttribute("data-cadence"),
        cadenceLabel:
          cadenceLabels[cadenceBtn.getAttribute("data-cadence")] ||
          cadenceBtn.textContent.trim(),
      };
    }

    function sellingPlanForCadence(cadence) {
      var plans = (catalog && catalog.selling_plans) || {};
      if (plans[cadence]) return plans[cadence];
      // Fuzzy fallback if Liquid keys differ
      var keys = Object.keys(plans);
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i].toLowerCase();
        if (
          cadence === "biweekly" &&
          (k.indexOf("bi") >= 0 ||
            k.indexOf("2") >= 0 ||
            k.indexOf("fortnight") >= 0)
        ) {
          return plans[keys[i]];
        }
        if (
          cadence === "weekly" &&
          k.indexOf("week") >= 0 &&
          k.indexOf("2") < 0 &&
          k.indexOf("bi") < 0
        ) {
          return plans[keys[i]];
        }
        if (cadence === "monthly" && k.indexOf("month") >= 0)
          return plans[keys[i]];
      }
      return null;
    }

    function refresh() {
      var s = state();
      syncStemLockedAddons(root, s.stems);
      var addOns = selectedAddOnsIn(root);
      var total =
        s.price +
        addOns.reduce(function (n, a) {
          return n + a.price;
        }, 0);
      var note = qs("[data-color-note]", root);
      if (note) note.textContent = colorCadenceNote(s);
      var summary = qs("[data-sub-summary]", root);
      var totalEl = qs("[data-sub-total]", root);
      if (summary)
        summary.textContent =
          s.stems + " stems · " + s.cadenceLabel + " · " + s.colorLabel;
      if (totalEl) totalEl.textContent = moneyFromCents(total);

      var hero = qs("[data-sub-hero]", root);
      if (hero) {
        var key = "data-hero-" + s.color;
        var src =
          hero.getAttribute(key) || hero.getAttribute("data-hero-default");
        if (src) hero.src = src;
      }
    }

    qsa("[data-sub-colors] [data-color]", root).forEach(function (btn) {
      btn.addEventListener("click", function () {
        qsa("[data-sub-colors] [data-color]", root).forEach(function (b) {
          b.classList.toggle("is-active", b === btn);
          b.setAttribute("aria-pressed", b === btn ? "true" : "false");
        });
        refresh();
      });
    });
    qsa("[data-sub-bundles] [data-stems]", root).forEach(function (btn) {
      btn.addEventListener("click", function () {
        if (btn.disabled) return;
        qsa("[data-sub-bundles] [data-stems]", root).forEach(function (b) {
          b.classList.toggle("is-active", b === btn);
          b.setAttribute("aria-pressed", b === btn ? "true" : "false");
        });
        refresh();
      });
    });
    qsa("[data-sub-cadence] [data-cadence]", root).forEach(function (btn) {
      btn.addEventListener("click", function () {
        qsa("[data-sub-cadence] [data-cadence]", root).forEach(function (b) {
          b.classList.toggle("is-active", b === btn);
          b.setAttribute("aria-pressed", b === btn ? "true" : "false");
        });
        refresh();
      });
    });
    bindAddonClicks(root, refresh);
    refresh();

    qs("[data-builder-atc]", root).addEventListener("click", function () {
      var s = state();
      var entry = (catalog && catalog[s.stems]) || {};
      var variantId = entry.variant_id;
      if (!variantId) {
        alert(
          "Subscription variant not found for " +
            s.stems +
            " stems. Please refresh and try again.",
        );
        return;
      }
      var sellingPlan = sellingPlanForCadence(s.cadence);
      if (!sellingPlan) {
        alert(
          "A delivery plan is required for subscriptions. Please choose Every 2 Weeks or Every Month.",
        );
        return;
      }

      var deliveryEl = qs("[data-first-delivery]", root);
      var delivery =
        (deliveryEl &&
          deliveryEl.getAttribute("data-date-picked") === "1" &&
          (deliveryEl.getAttribute("data-date") || deliveryEl.value)) ||
        "";
      if (!delivery) {
        if (deliveryEl) {
          deliveryEl.focus();
          deliveryEl.click();
        }
        alert("Please choose your first delivery date.");
        return;
      }

      var groupId = "rf-" + Date.now();
      var props = {
        _group_id: groupId,
        Bundle: s.stems + " stems",
        Color: s.colorLabel,
        Frequency: s.cadenceLabel,
        "Delivery Date": delivery,
      };

      var line = {
        id: parseInt(variantId, 10),
        quantity: 1,
        selling_plan: parseInt(sellingPlan, 10),
        properties: props,
      };

      var items = [line].concat(
        selectedAddOnsIn(root).map(function (a) {
          return {
            id: a.id,
            quantity: 1,
            properties: {
              _group_id: groupId,
              _addon: a.addonId || "1",
              _is_addon: "true",
            },
          };
        }),
      );

      var btn = qs("[data-builder-atc]", root);
      btn.disabled = true;
      window.RF.addToCart(items, true)
        .catch(function (err) {
          var msg =
            (err && (err.description || err.message)) ||
            "Could not add subscription to cart. Please try again.";
          alert(msg);
        })
        .finally(function () {
          btn.disabled = false;
        });
    });
  }

  function initBuilders() {
    var custom = qs('[data-rf-builder="custom"]');
    if (custom) initCustomBuilder(custom);
    var weddings = qs('[data-rf-builder="weddings"]');
    if (weddings) initWeddingsBuilder(weddings);
    var subs = qs('[data-rf-builder="subscriptions"]');
    if (subs) initSubscriptionsBuilder(subs);
  }

  function initAboutSlideshow() {
    var root = qs("[data-about-slideshow]");
    if (!root) return;
    var slides = qsa("[data-about-slide]", root);
    var dots = qsa("[data-about-dot]", root);
    if (slides.length < 2) return;
    var index = 0;

    function show(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (slide, n) {
        slide.classList.toggle("is-active", n === index);
      });
      dots.forEach(function (dot, n) {
        dot.classList.toggle("is-active", n === index);
      });
    }

    var prev = qs("[data-about-prev]", root);
    var next = qs("[data-about-next]", root);
    if (prev)
      prev.addEventListener("click", function () {
        show(index - 1);
      });
    if (next)
      next.addEventListener("click", function () {
        show(index + 1);
      });
    dots.forEach(function (dot) {
      dot.addEventListener("click", function () {
        show(parseInt(dot.getAttribute("data-about-dot"), 10) || 0);
      });
    });
  }

  function openCardPrompt(root) {
    var dialog = qs("[data-card-prompt]", root) || qs("[data-card-prompt]");
    if (!dialog) {
      alert(
        "Please write a short note for your holiday card before adding to cart.",
      );
      var field = qs("#card-message", root) || qs("[data-card-message]", root);
      if (field) {
        field.focus();
        field.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    dialog.hidden = false;
    var focusBtn = qs("[data-card-prompt-focus]", dialog);
    var dismiss = function () {
      dialog.hidden = true;
    };
    qsa("[data-card-prompt-dismiss]", dialog).forEach(function (el) {
      el.onclick = dismiss;
    });
    if (focusBtn) {
      focusBtn.onclick = function () {
        dismiss();
        var field =
          qs("#card-message", root) || qs("[data-card-message]", root);
        if (field) {
          field.focus();
          field.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      };
    }
  }

  function initExtraStems(root, updateTotal) {
    var wrap = qs("[data-extra-stems]", root);
    if (!wrap)
      return {
        totalCents: function () {
          return 0;
        },
        lines: function () {
          return [];
        },
      };

    var selection = {};

    function render() {
      var dozens = 0;
      Object.keys(selection).forEach(function (k) {
        dozens += selection[k];
      });
      var price =
        parseInt(wrap.getAttribute("data-extra-stem-price"), 10) || 1488;
      var summary = qs("[data-extra-stems-summary]", wrap);
      if (summary) {
        summary.textContent =
          dozens > 0
            ? "+" + dozens * 12 + " stems · " + moneyFromCents(dozens * price)
            : "any color · $14.88 per dozen";
        summary.classList.toggle("is-active", dozens > 0);
      }
      qsa("[data-extra-stem]", wrap).forEach(function (el) {
        var id = el.getAttribute("data-stem-id");
        var n = selection[id] || 0;
        var badge = qs("[data-extra-stem-badge]", el);
        var qty = qs("[data-extra-stem-qty]", el);
        el.classList.toggle("is-active", n > 0);
        if (badge) {
          badge.hidden = n <= 0;
          badge.textContent = "×" + n;
        }
        if (qty) qty.hidden = n <= 0;
      });
      if (updateTotal) updateTotal();
    }

    function bump(id, delta) {
      var n = (selection[id] || 0) + delta;
      if (n <= 0) delete selection[id];
      else selection[id] = n;
      render();
    }

    var toggle = qs("[data-extra-stems-toggle]", wrap);
    var panel = qs("[data-extra-stems-panel]", wrap);
    if (toggle) {
      toggle.addEventListener("click", function () {
        var open = toggle.getAttribute("aria-expanded") !== "true";
        toggle.setAttribute("aria-expanded", open ? "true" : "false");
        if (panel) panel.hidden = !open;
        wrap.classList.toggle("is-collapsed", !open);
      });
    }

    qsa("[data-extra-stem]", wrap).forEach(function (el) {
      var id = el.getAttribute("data-stem-id");
      var add = qs("[data-extra-stem-add]", el);
      var plus = qs("[data-extra-stem-plus]", el);
      var minus = qs("[data-extra-stem-minus]", el);
      if (add)
        add.addEventListener("click", function () {
          bump(id, 1);
        });
      if (plus)
        plus.addEventListener("click", function () {
          bump(id, 1);
        });
      if (minus)
        minus.addEventListener("click", function () {
          bump(id, -1);
        });
    });

    render();
    return {
      totalCents: function () {
        var price =
          parseInt(wrap.getAttribute("data-extra-stem-price"), 10) || 1488;
        return Object.keys(selection).reduce(function (s, k) {
          return s + selection[k] * price;
        }, 0);
      },
      lines: function (groupId) {
        return qsa("[data-extra-stem]", wrap)
          .filter(function (el) {
            return (selection[el.getAttribute("data-stem-id")] || 0) > 0;
          })
          .map(function (el) {
            var id = el.getAttribute("data-stem-id");
            var label = el.getAttribute("data-label") || "Extra stems";
            return {
              id: parseInt(el.getAttribute("data-variant-id"), 10),
              quantity: selection[id],
              properties: {
                _group_id: groupId,
                _addon: "extra-stem-" + id,
                _is_addon: "true",
                _label: label + " — 12 extra stems",
              },
            };
          });
      },
    };
  }

  function initWelcomePopup() {
    var root = qs("[data-welcome-popup]");
    if (!root) return;
    // AAA subdomains / products with rosefarmers.suppress_popups (snippets/popup-suppression.liquid)
    if (window.RF && window.RF.suppressPopups) return;
    try {
      if (window.sessionStorage.getItem("rf-welcome-popup-seen")) return;
    } catch (e) {}

    var panel = qs("[data-welcome-panel]", root);
    var form = qs("[data-welcome-form]", root) || qs("form", root);
    var success = qs("[data-welcome-success]", root);

    function close() {
      root.classList.remove("is-open");
      window.setTimeout(function () {
        root.hidden = true;
      }, 400);
      try {
        window.sessionStorage.setItem("rf-welcome-popup-seen", "1");
      } catch (e) {}
    }

    qsa("[data-welcome-close]", root).forEach(function (btn) {
      btn.addEventListener("click", close);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !root.hidden) close();
    });

    if (form) {
      form.addEventListener("submit", function () {
        // Let Shopify customer form POST; also show success UI for SPA-like feel when possible
        window.setTimeout(function () {
          if (success) success.hidden = false;
          if (form) form.hidden = true;
        }, 0);
      });
    }

    window.setTimeout(function () {
      root.hidden = false;
      requestAnimationFrame(function () {
        root.classList.add("is-open");
      });
    }, 1200);
  }

  function initProduct() {
    var root = qs("[data-product-section]");
    if (!root) return;
    var form = qs("[data-product-form]", root) || qs("#ProductForm", root);
    if (!form) return;

    var variantInput = qs("[data-product-variant-id]", form);
    var priceEl = qs("[data-product-price]", root);
    var totalWrap = qs("[data-product-total]", root);
    var totalValue = qs("[data-product-total-value]", root);
    var mainMedia = qs("[data-product-main-media] img", root);
    var extraStems;

    qsa("[data-product-thumbs] [data-thumb-src]", root).forEach(function (btn) {
      var preloadSrc = btn.getAttribute("data-thumb-src");
      if (!preloadSrc) return;
      var warm = new Image();
      warm.decoding = "async";
      warm.src = preloadSrc;
    });

    qsa("[data-product-thumbs] [data-thumb-src]", root).forEach(function (btn) {
      btn.addEventListener("click", function () {
        qsa("[data-product-thumbs] .rf-product__thumb", root).forEach(
          function (b) {
            b.classList.toggle("is-active", b === btn);
          },
        );
        if (!mainMedia) return;
        var src = btn.getAttribute("data-thumb-src");
        if (!src) return;
        mainMedia.removeAttribute("srcset");
        mainMedia.removeAttribute("sizes");
        mainMedia.src = src;
        var thumbImg = qs("img", btn);
        if (thumbImg && thumbImg.alt) mainMedia.alt = thumbImg.alt;
      });
    });

    function currentStemCount() {
      var active = qs("[data-variant-pills] .is-active", root);
      if (active) {
        var stems = parseInt(active.getAttribute("data-stems"), 10);
        if (stems) return stems;
        return stemCountFromLabel(active.textContent);
      }
      if (variantInput) {
        try {
          var product = JSON.parse(qs("[data-product-json]", root).textContent);
          var id = String(variantInput.value);
          var variant = (product.variants || []).find(function (v) {
            return String(v.id) === id;
          });
          if (variant)
            return stemCountFromLabel(variant.title || variant.option1 || "");
        } catch (e) {}
      }
      return 0;
    }

    function updateSizeGatedAddons() {
      syncSizeGatedAddons(root, currentStemCount());
    }

    qsa("[data-variant-pills] [data-variant-id]", root).forEach(function (btn) {
      btn.addEventListener("click", function () {
        qsa("[data-variant-pills] [data-variant-id]", root).forEach(
          function (b) {
            b.classList.toggle("is-active", b === btn);
          },
        );
        if (variantInput)
          variantInput.value = btn.getAttribute("data-variant-id");
        if (priceEl)
          priceEl.textContent = btn.getAttribute("data-variant-price");
        updateSizeGatedAddons();
        updateTotal();
      });
    });

    updateSizeGatedAddons();

    qsa("[data-addon]", root).forEach(function (el) {
      el.addEventListener("click", function (e) {
        if (el.matches("button")) {
          e.preventDefault();
          var kind = el.getAttribute("data-addon-kind");
          if (kind === "vase") {
            var was = el.classList.contains("is-active");
            qsa('[data-addon-kind="vase"]', root).forEach(function (b) {
              b.classList.remove("is-active");
            });
            if (!was) el.classList.add("is-active");
          } else if (kind === "greenery") {
            el.classList.toggle("is-active");
          }
          updateTotal();
        }
      });
      el.addEventListener("change", function () {
        if (el.getAttribute("data-toggles-card") != null) {
          toggleCardFields(root, el.checked);
        }
        var label = el.closest(".rf-addon");
        if (label) label.classList.toggle("is-active", el.checked);
        updateTotal();
      });
    });

    function selectedAddOns() {
      var items = [];
      qsa("[data-addon]", root).forEach(function (el) {
        var active = el.matches("button")
          ? el.classList.contains("is-active")
          : el.checked;
        if (!active || el.hidden) return;
        items.push({
          id: parseInt(el.getAttribute("data-variant-id"), 10),
          quantity: 1,
          price: parseInt(el.getAttribute("data-price"), 10) || 0,
          addonId: el.getAttribute("data-addon-id"),
        });
      });
      return items;
    }

    function baseCents() {
      var jsonEl = qs("[data-product-json]");
      try {
        var product = JSON.parse(jsonEl.textContent);
        var id = variantInput ? String(variantInput.value) : "";
        var variant =
          (product.variants || []).find(function (v) {
            return String(v.id) === id;
          }) || product.variants[0];
        return variant ? variant.price : 0;
      } catch (e) {
        return 0;
      }
    }

    function updateTotal() {
      var addOns = selectedAddOns();
      var extra = extraStems ? extraStems.totalCents() : 0;
      var total =
        baseCents() +
        addOns.reduce(function (s, a) {
          return s + a.price;
        }, 0) +
        extra;
      if (totalWrap) totalWrap.hidden = addOns.length === 0 && extra === 0;
      if (totalValue) totalValue.textContent = moneyFromCents(total);
    }

    extraStems = initExtraStems(root, updateTotal);
    var subUpsell = initSubUpsell(root);

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (!window.RF || !window.RF.addToCart) return;

      var addOns = selectedAddOns();
      var holidayOn = addOns.some(function (a) {
        return a.addonId === "holiday-card";
      });
      var cardMessage = (qs("[data-card-message]", form) || {}).value || "";
      if (holidayOn && !String(cardMessage).trim()) {
        openCardPrompt(root);
        return;
      }

      var groupId = "rf-" + Date.now();
      var deliveryEl = qs("[data-delivery-date]", form);
      var delivery =
        (deliveryEl &&
          deliveryEl.getAttribute("data-date-picked") === "1" &&
          (deliveryEl.getAttribute("data-date") || deliveryEl.value)) ||
        "";
      var cardFrom = (qs("[data-card-from]", form) || {}).value || "";
      var props = { _group_id: groupId };
      if (delivery) props["Delivery Date"] = delivery;
      // No paid card, no note -- even if a restored form still holds text.
      if (holidayOn) {
        if (cardFrom) props.From = cardFrom;
        if (cardMessage) props["Card Message"] = cardMessage;
      }
      // Template-specific line properties, e.g. promo color tiles: <input data-line-prop="Color">
      qsa("[data-line-prop]", form).forEach(function (el) {
        if (el.value) props[el.getAttribute("data-line-prop")] = el.value;
      });

      if (!delivery) {
        if (deliveryEl) {
          deliveryEl.focus();
          deliveryEl.click();
        }
        alert("Please choose a delivery date.");
        return;
      }

      var parentTitle = "";
      try {
        var productJson = JSON.parse(
          qs("[data-product-json]", root).textContent,
        );
        parentTitle = (productJson && productJson.title) || "";
      } catch (err) {}

      var items = [
        {
          id: parseInt(variantInput.value, 10),
          quantity: 1,
          properties: props,
        },
      ]
        .concat(
          addOns.map(function (a) {
            return {
              id: a.id,
              quantity: 1,
              properties: {
                _group_id: groupId,
                _addon: a.addonId || "1",
                _is_addon: "true",
              },
            };
          }),
        )
        .concat(extraStems ? extraStems.lines(groupId) : []);

      var stems = currentStemCount();
      var offerUpsell =
        stems === 24 &&
        !form.hasAttribute("data-no-upsell") &&
        upsellModalHasOptions();

      var btn = qs("[data-add-to-cart]", form);
      if (btn) btn.disabled = true;

      // Exclusive-offer templates: empty the cart, add just this bouquet, go to checkout.
      // (main-product-promo renders data-checkout-direct="false" when its setting is off.)
      if (
        form.hasAttribute("data-checkout-direct") &&
        form.getAttribute("data-checkout-direct") !== "false"
      ) {
        fetch("/cart/clear.js", { method: "POST", credentials: "same-origin" })
          .then(function () {
            return window.RF.addToCart(items, false);
          })
          .then(goToCheckout)
          .catch(function () {
            if (btn) btn.disabled = false;
          });
        return;
      }

      function addOneTime(skipUpsell) {
        var upsell = offerUpsell && !skipUpsell;
        if (btn) btn.disabled = true;
        window.RF.addToCart(items, !upsell)
          .then(function () {
            if (upsell) {
              openUpsellModal({ groupId: groupId, parentTitle: parentTitle });
            }
          })
          .catch(function () {})
          .finally(function () {
            if (btn) btn.disabled = false;
          });
      }

      // Subscription colors at 24/48 stems: offer to switch to a subscription first.
      // Declining adds the one-time order without the 12-stem popup on top (Last chance offers it at checkout).
      if (subUpsell && subUpsell.eligible(stems)) {
        if (btn) btn.disabled = false;
        subUpsell.open({
          stems: stems,
          bouquetProps: props,
          addOnLines: items.slice(1),
          onDecline: function () {
            addOneTime(true);
          },
        });
        return;
      }

      addOneTime(false);
    });

    updateTotal();
  }

  /**
   * "Switch to a subscription" popup on PDP Add to Cart (snippets/subscription-upsell-modal.liquid).
   * Only for 24/48 stems of a subscription color, once per session, never with popups suppressed.
   * Subscribing adds the monthly subscription at the same stem count in place of the bouquet,
   * with the same line properties (delivery date, card) and add-on lines.
   * Returns null when the product has no popup.
   */
  var SUB_UPSELL_SEEN_KEY = "rf-sub-upsell-seen";

  function initSubUpsell(root) {
    var modal = qs("[data-sub-upsell-modal]", root);
    if (!modal) return null;
    var catalog;
    try {
      catalog = JSON.parse(qs("[data-sub-upsell-catalog]", modal).textContent);
    } catch (e) {
      return null;
    }
    var ctx = null; // { stems, bouquetProps, addOnLines, onDecline }

    // document.body so the fixed overlay isn't clipped by the product section
    document.body.appendChild(modal);

    // Testing: ?subupsell on the product URL clears "seen" so the popup shows again this session
    if (/[?&]subupsell\b/.test(window.location.search)) {
      try {
        window.sessionStorage.removeItem(SUB_UPSELL_SEEN_KEY);
      } catch (e) {}
    }

    function seen() {
      try {
        return !!window.sessionStorage.getItem(SUB_UPSELL_SEEN_KEY);
      } catch (e) {
        return false;
      }
    }

    /** Monthly plan vs the one-time bouquet + shipping, or null when there's no monthly plan. */
    function offerFor(stems) {
      var sub = catalog.sub && catalog.sub[stems];
      var one = catalog.oneTime && catalog.oneTime[stems];
      var plan = sub && sub.plans && sub.plans.monthly;
      if (!one || !plan) return null;
      var oneTotal = one.price + (catalog.shipping || 0);
      return {
        variantId: sub.id,
        planId: plan.id,
        subPrice: plan.price,
        oneRoses: one.price,
        oneTotal: oneTotal,
        save: oneTotal - plan.price,
      };
    }

    function setText(attr, text) {
      qsa("[" + attr + "]", modal).forEach(function (el) {
        el.textContent = text;
      });
    }

    function render(o) {
      setText("data-sub-upsell-stems", String(ctx.stems));
      setText("data-sub-upsell-sub-price", moneyFromCents(o.subPrice));
      setText("data-sub-upsell-ot-roses", moneyFromCents(o.oneRoses));
      setText("data-sub-upsell-ot-ship", moneyFromCents(catalog.shipping || 0));
      setText("data-sub-upsell-ot-total", moneyFromCents(o.oneTotal));
      setText("data-sub-upsell-skip-total", moneyFromCents(o.oneTotal));
      setText("data-sub-upsell-save", moneyFromCents(o.save));
      var yearly = qs("[data-sub-upsell-yearly]", modal);
      if (yearly) {
        yearly.innerHTML =
          "Delivered monthly, that’s <strong>" +
          moneyFromCents(Math.round((o.save * 12) / 100) * 100).replace(
            /\.00$/,
            "",
          ) +
          " saved</strong> over a year.";
      }
    }

    function close() {
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      modal.classList.remove("is-open");
      document.documentElement.classList.remove("rf-upsell-open");
      ctx = null;
    }

    function subscribe(confirmBtn) {
      var o = offerFor(ctx.stems);
      if (!o || !window.RF || !window.RF.addToCart) return;
      var props = {};
      Object.keys(ctx.bouquetProps).forEach(function (k) {
        props[k] = ctx.bouquetProps[k];
      });
      props.Bundle = ctx.stems + " stems";
      props.Color = catalog.colorLabel;
      props.Frequency = "Every Month";
      var items = [
        {
          id: o.variantId,
          quantity: 1,
          selling_plan: o.planId,
          properties: props,
        },
      ].concat(ctx.addOnLines);
      confirmBtn.disabled = true;
      window.RF.addToCart(items, true)
        .then(close)
        .catch(function (err) {
          alert(
            (err && (err.description || err.message)) ||
              "Could not add the subscription to your cart. Please try again.",
          );
        })
        .finally(function () {
          confirmBtn.disabled = false;
        });
    }

    modal.addEventListener("click", function (e) {
      if (!ctx) return;
      if (e.target.closest("[data-sub-upsell-dismiss]")) {
        close();
        return;
      }
      if (e.target.closest("[data-sub-upsell-skip]")) {
        var decline = ctx.onDecline;
        close();
        decline();
        return;
      }
      var confirmBtn = e.target.closest("[data-sub-upsell-confirm]");
      if (confirmBtn && !confirmBtn.disabled) subscribe(confirmBtn);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && modal.classList.contains("is-open")) close();
    });

    return {
      eligible: function (stems) {
        if (window.RF && window.RF.suppressPopups) return false;
        if (seen()) return false;
        return !!offerFor(stems);
      },
      open: function (opts) {
        ctx = opts;
        try {
          window.sessionStorage.setItem(SUB_UPSELL_SEEN_KEY, "1");
        } catch (e) {}
        render(offerFor(opts.stems));
        modal.hidden = false;
        modal.setAttribute("aria-hidden", "false");
        modal.classList.add("is-open");
        document.documentElement.classList.add("rf-upsell-open");
      },
    };
  }

  function initHero() {
    var root = qs("[data-rf-hero]");
    if (!root) return;
    var slides = qsa("[data-hero-slide]", root);
    if (slides.length < 2) return;
    var index = 0;
    var timer = null;
    var interval =
      parseInt(root.getAttribute("data-hero-interval"), 10) || 7000;
    var headingEl = qs("[data-hero-heading]", root);
    var textEl = qs("[data-hero-text]", root);
    var copyWrap = qs("[data-hero-copy]", root);
    var reviewsWrap = qs("[data-hero-reviews]", root);
    var ctas = qs("[data-hero-ctas]", root);
    var primary = qs("[data-hero-cta-primary]", root);
    var primaryLabel = qs("[data-hero-cta-primary-label]", root);
    var secondary = qs("[data-hero-cta-secondary]", root);
    var secondaryLabel = qs("[data-hero-cta-secondary-label]", root);
    var secondarySub = qs("[data-hero-cta-secondary-sub]", root);

    function syncVideos(activeSlide) {
      slides.forEach(function (slide) {
        var videos = qsa("video", slide);
        videos.forEach(function (video) {
          if (slide === activeSlide) {
            video.muted = true;
            var playPromise = video.play();
            if (playPromise && typeof playPromise.catch === "function") {
              playPromise.catch(function () {});
            }
          } else {
            video.pause();
          }
        });
      });
    }

    function applyDualCtas(ctaLabel, ctaLink, secLabel, secLink, secSub) {
      if (ctas) ctas.hidden = false;
      if (primary) {
        primary.hidden = false;
        if (ctaLink) primary.href = ctaLink;
        if (primaryLabel && ctaLabel) primaryLabel.textContent = ctaLabel;
      }
      if (secondary) {
        secondary.hidden = !secLabel;
        if (secLink) secondary.href = secLink;
        if (secondaryLabel && secLabel) secondaryLabel.textContent = secLabel;
        if (secondarySub) {
          secondarySub.textContent = secSub;
          secondarySub.hidden = !secSub;
        }
      }
    }

    function applySlide(slide) {
      var mode = slide.getAttribute("data-slide-mode") || "copy";
      var heading = slide.getAttribute("data-slide-heading") || "";
      var text = slide.getAttribute("data-slide-text") || "";
      var ctaLabel = slide.getAttribute("data-slide-cta-label") || "";
      var ctaLink = slide.getAttribute("data-slide-cta-link") || "";
      var secLabel = slide.getAttribute("data-slide-cta-secondary-label") || "";
      var secLink = slide.getAttribute("data-slide-cta-secondary-link") || "";
      var secSub = slide.getAttribute("data-slide-cta-secondary-sub") || "";

      if (reviewsWrap) reviewsWrap.hidden = mode !== "reviews";
      if (copyWrap) copyWrap.hidden = mode === "reviews";

      if (mode !== "reviews") {
        if (headingEl && heading) {
          headingEl.textContent = heading;
          headingEl.classList.remove("rf-fade-in");
          void headingEl.offsetWidth;
          headingEl.classList.add("rf-fade-in");
        }
        if (textEl) {
          textEl.textContent = text;
          textEl.hidden = !text;
          textEl.classList.remove("rf-fade-in");
          void textEl.offsetWidth;
          textEl.classList.add("rf-fade-in");
        }
      }

      if (mode === "florist") {
        if (ctas) ctas.hidden = false;
        if (primary && ctaLabel) {
          primary.hidden = false;
          if (ctaLink) primary.href = ctaLink;
          if (primaryLabel) primaryLabel.textContent = ctaLabel;
        }
        if (secondary) secondary.hidden = true;
      } else if (mode === "copy" || mode === "reviews") {
        applyDualCtas(ctaLabel, ctaLink, secLabel, secLink, secSub);
      }

      syncVideos(slide);
    }

    function show(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (slide, n) {
        slide.classList.toggle("is-active", n === index);
      });
      applySlide(slides[index]);
    }

    function next(delta) {
      show(index + delta);
      restart();
    }

    function restart() {
      if (timer) clearInterval(timer);
      timer = setInterval(function () {
        show(index + 1);
      }, interval);
    }

    var prev = qs("[data-hero-prev]", root);
    var nextBtn = qs("[data-hero-next]", root);
    if (prev)
      prev.addEventListener("click", function () {
        next(-1);
      });
    if (nextBtn)
      nextBtn.addEventListener("click", function () {
        next(1);
      });
    applySlide(slides[0]);
    restart();
  }

  function initFeelings() {
    var root = qs("[data-rf-feelings]");
    if (!root) return;
    var track = qs("[data-feelings-track]", root);
    if (!track) return;
    var prev = qs("[data-feelings-prev]", root);
    var next = qs("[data-feelings-next]", root);
    if (prev) {
      prev.addEventListener("click", function () {
        track.scrollBy({ left: -340, behavior: "smooth" });
      });
    }
    if (next) {
      next.addEventListener("click", function () {
        track.scrollBy({ left: 340, behavior: "smooth" });
      });
    }
  }

  function initInstagram() {
    var root = qs("[data-rf-instagram]");
    if (!root) return;
    var feedId = root.getAttribute("data-feed-id");
    var grid = qs("[data-ig-grid]", root);
    if (!feedId || !grid) return;

    fetch("https://feeds.behold.so/" + encodeURIComponent(feedId))
      .then(function (r) {
        if (!r.ok) throw new Error("feed");
        return r.json();
      })
      .then(function (data) {
        var posts = (data && data.posts ? data.posts : []).slice(0, 8);
        if (!posts.length) return;
        grid.innerHTML = posts
          .map(function (p) {
            var src =
              (p.sizes && p.sizes.medium && p.sizes.medium.mediaUrl) ||
              (p.sizes && p.sizes.small && p.sizes.small.mediaUrl) ||
              p.thumbnailUrl ||
              p.mediaUrl ||
              "";
            var href = p.permalink || "https://instagram.com/rosefarmers";
            if (!src) {
              return (
                '<a class="rf-ig__cell rf-ig__cell--placeholder" href="' +
                href +
                '" target="_blank" rel="noopener noreferrer"></a>'
              );
            }
            return (
              '<a class="rf-ig__cell" href="' +
              href +
              '" target="_blank" rel="noopener noreferrer">' +
              '<img src="' +
              src +
              '" alt="" loading="lazy">' +
              "</a>"
            );
          })
          .join("");
      })
      .catch(function () {
        /* keep placeholders */
      });
  }

  /**
   * IFS Instafeed app injects #instafeed into the footer group (full bleed).
   * Move that feed into our homepage Instagram section so it shares page width.
   */
  function initInstagramApp() {
    var root = qs("[data-rf-instagram-app]");
    if (!root) return;
    var mount = qs("[data-rf-instafeed-mount]", root);
    if (!mount) return;

    function findInjectedFeed() {
      var nodes = document.querySelectorAll("#instafeed, .instafeed");
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (root.contains(el)) continue;
        if (
          el.querySelector(
            ".instagram-grid-container, .instagram-grid-item, .ifs-img",
          )
        ) {
          return el;
        }
      }
      var grid = document.querySelector(
        ".shopify-section-group-footer-group .instagram-grid-container",
      );
      if (!grid || root.contains(grid)) return null;
      return grid.closest("#instafeed, .instafeed") || grid;
    }

    function adopt() {
      var feed = findInjectedFeed();
      if (!feed || !mount.parentNode) return false;
      feed.classList.add("rf-ig-app__feed");
      mount.replaceWith(feed);
      return true;
    }

    if (adopt()) return;

    var observer = new MutationObserver(function () {
      if (adopt()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () {
      observer.disconnect();
    }, 20000);
  }

  /**
   * Farm shipping calendar:
   * - No weekend delivery (Sat/Sun disabled)
   * - 3-day farm window (vendor A):
   *   Mon→Thu, Tue→Fri, Wed→Mon, Thu→Mon, Fri→Tue, Sat→Wed
   */
  function pad2(n) {
    return n < 10 ? "0" + n : String(n);
  }

  function formatISODate(d) {
    return (
      d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate())
    );
  }

  function formatDisplayDate(d) {
    return d.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function addDays(d, n) {
    var next = new Date(d.getTime());
    next.setDate(next.getDate() + n);
    return next;
  }

  function nyPartsNow() {
    var parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    var map = {};
    parts.forEach(function (p) {
      map[p.type] = p.value;
    });
    var year = parseInt(map.year, 10);
    var month = parseInt(map.month, 10);
    var day = parseInt(map.day, 10);
    return {
      year: year,
      month: month,
      day: day,
      hour: parseInt(map.hour, 10),
      minute: parseInt(map.minute, 10),
      dow: new Date(year, month - 1, day).getDay(),
    };
  }

  function getCurrentShippingDay() {
    var ny = nyPartsNow();
    var ships = new Date(ny.year, ny.month - 1, ny.day);
    var dow = ny.dow;
    var pastCutoff = ny.hour > 11 || (ny.hour === 11 && ny.minute >= 40);

    if (dow === 0) {
      ships = addDays(ships, 1);
    } else if (dow >= 1 && dow <= 5 && pastCutoff) {
      ships = addDays(ships, 1);
    } else if (
      dow === 6 &&
      (ny.hour > 9 || (ny.hour === 9 && ny.minute >= 40))
    ) {
      ships = addDays(ships, 2);
    }

    // If cutoff pushed into Sunday, move to Monday
    if (ships.getDay() === 0) ships = addDays(ships, 1);
    return ships;
  }

  function deliveryFromShipDay(shipDate) {
    var arrival = new Date(shipDate.getTime());
    switch (arrival.getDay()) {
      case 1: // Mon → Thu
      case 2: // Tue → Fri
        arrival = addDays(arrival, 3);
        break;
      case 3: // Wed → Mon
        arrival = addDays(arrival, 5);
        break;
      case 4: // Thu → Mon
      case 5: // Fri → Tue
        arrival = addDays(arrival, 4);
        break;
      case 6: // Sat → Wed
        arrival = addDays(arrival, 4);
        break;
      case 0: // Sun treated as Mon ship → Thu
        arrival = addDays(arrival, 4);
        break;
      default:
        arrival = addDays(arrival, 3);
    }
    return skipWeekend(arrival);
  }

  function skipWeekend(date) {
    var d = startOfDay(date);
    while (d.getDay() === 0 || d.getDay() === 6) {
      d = addDays(d, 1);
    }
    if (
      window.RF &&
      window.RF.noInventoryDates &&
      window.RF.noInventoryDates[formatISODate(d)]
    ) {
      return skipWeekend(addDays(d, 1));
    }
    if (window.RF && Array.isArray(window.RF.holidays)) {
      while (
        window.RF.holidays.indexOf(formatISODate(d)) >= 0 ||
        d.getDay() === 0 ||
        d.getDay() === 6
      ) {
        d = addDays(d, 1);
      }
    }
    return d;
  }

  function getEarliestArrivalDay(shipSpeed, vendor) {
    vendor = vendor || "A";
    var ships = getCurrentShippingDay();
    if (vendor === "D") {
      return skipToVendorDelivery(addDays(ships, 6), "D");
    }
    return deliveryFromShipDay(ships);
  }

  function skipToVendorDelivery(date, vendor) {
    var d = startOfDay(date);
    var guard = 0;
    while (guard++ < 21) {
      if (isDeliveryDateAllowed(d, null, vendor)) return d;
      d = addDays(d, 1);
    }
    return d;
  }

  function isDeliveryDateAllowed(date, minDate, vendor) {
    var d = startOfDay(date);
    var dow = d.getDay();
    vendor = vendor || "A";
    if (vendor === "D") {
      if (dow !== 3 && dow !== 4) return false; // Wed / Thu only
    } else if (dow === 0 || dow === 6) {
      return false;
    }
    if (minDate && d < startOfDay(minDate)) return false;
    var iso = formatISODate(d);
    if (
      window.RF &&
      window.RF.noInventoryDates &&
      window.RF.noInventoryDates[iso]
    )
      return false;
    if (
      window.RF &&
      Array.isArray(window.RF.holidays) &&
      window.RF.holidays.indexOf(iso) >= 0
    ) {
      return false;
    }
    return true;
  }

  function initDeliveryDatePickers() {
    window.RF = window.RF || {};
    window.RF.holidays = window.RF.holidays || [];
    window.RF.noInventoryDates = window.RF.noInventoryDates || {};
    window.RF.getEarliestArrivalDay = getEarliestArrivalDay;

    window.RF.closestDeliveryDate = function (
      momentDate,
      customTester,
      vendor,
    ) {
      vendor = vendor || "A";
      var useMoment =
        typeof moment !== "undefined" &&
        momentDate &&
        typeof momentDate.clone === "function";
      var cursor = useMoment
        ? momentDate.clone()
        : startOfDay(
            momentDate instanceof Date ? momentDate : new Date(momentDate),
          );
      var guard = 0;
      while (guard++ < 60) {
        var asDate = useMoment ? cursor.toDate() : cursor;
        var ok = isDeliveryDateAllowed(asDate, null, vendor);
        if (ok && typeof customTester === "function") {
          ok = customTester(useMoment ? cursor : asDate) !== false;
        }
        if (ok) return useMoment ? cursor : asDate;
        if (useMoment) cursor.add(1, "days");
        else cursor = addDays(cursor, 1);
      }
      return cursor;
    };

    window.RF.initAddonDescriptions = function (containers) {
      if (!window.jQuery) return;
      var $ = window.jQuery;
      var hideDescriptions = function () {
        $(".sub-item-image-container").removeClass("show-description");
      };
      $(containers)
        .find(".sub-item-image-container > *")
        .on("mouseover click", function () {
          hideDescriptions();
          $(this)
            .parents(".sub-item-image-container")
            .addClass("show-description");
        })
        .on("mouseout", function () {
          hideDescriptions();
        });
    };

    window.RF.initDeliveryDatePicker = function (config) {
      if (!config) return;
      var input = config.deliveryDateInput || config;
      if (!input || !input.tagName) return;
      bindDeliveryInput(input, config);
    };

    window.RF.setDeliveryDateInput = function (input, dateObj) {
      if (!input || !dateObj) return;
      var d = dateObj.toDate ? dateObj.toDate() : dateObj;
      d = startOfDay(d);
      input.value = formatDisplayDate(d);
      input.setAttribute("data-date", formatISODate(d));
      input.setAttribute("data-date-picked", "1");
      if (window.jQuery) window.jQuery(input).data("date", formatISODate(d));
      input.dispatchEvent(new Event("change", { bubbles: true }));
    };

    qsa("[data-delivery-date]").forEach(function (el) {
      bindDeliveryInput(el, { vendor: "A" });
    });
  }

  function bindDeliveryInput(input, config) {
    config = config || {};
    var vendor = config.vendor || input.getAttribute("data-vendor") || "A";
    var onSelect =
      typeof config.datePickCallback === "function"
        ? config.datePickCallback
        : null;
    var requirePick = config.requirePick !== false;
    var hasPicked = input.getAttribute("data-date-picked") === "1";

    if (input.getAttribute("data-rf-date-bound")) {
      // Re-init: update options on existing binding
      input.__rfDateConfig = config;
      input.__rfDateVendor = vendor;
      if (
        config.defaultDate &&
        config.overwriteValue !== false &&
        config.requirePick === false
      ) {
        window.RF.setDeliveryDateInput(input, config.defaultDate);
      }
      return;
    }

    input.setAttribute("data-rf-date-bound", "1");
    input.__rfDateConfig = config;
    input.__rfDateVendor = vendor;
    input.setAttribute("readonly", "readonly");
    input.setAttribute("autocomplete", "off");
    if (input.type === "date") input.type = "text";

    var minDate = config.minDate
      ? startOfDay(config.minDate)
      : getEarliestArrivalDay(config.shipSpeed, vendor);
    minDate = skipToVendorDelivery(minDate, vendor);

    var selected = minDate;
    if (config.defaultDate) {
      selected = startOfDay(config.defaultDate);
      if (!isDeliveryDateAllowed(selected, minDate, vendor)) {
        selected = minDate;
      }
    }

    function applyDate(d, silent) {
      selected = startOfDay(d);
      input.value = formatDisplayDate(selected);
      input.setAttribute("data-date", formatISODate(selected));
      if (window.jQuery)
        window.jQuery(input).data("date", formatISODate(selected));
      if (!silent) {
        hasPicked = true;
        input.setAttribute("data-date-picked", "1");
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    var existingIso =
      (window.jQuery && window.jQuery(input).data("date")) ||
      input.getAttribute("data-date") ||
      "";

    if (config.overwriteValue === false && (existingIso || input.value)) {
      // Account / server-rendered value — treat as already chosen.
      if (existingIso) {
        selected = startOfDay(
          new Date(String(existingIso).slice(0, 10) + "T12:00:00"),
        );
        input.setAttribute("data-date", formatISODate(selected));
        if (window.jQuery)
          window.jQuery(input).data("date", formatISODate(selected));
      }
      if (!input.value) applyDate(selected, true);
      hasPicked = true;
      input.setAttribute("data-date-picked", "1");
    } else if (!requirePick) {
      applyDate(selected, true);
      hasPicked = true;
      input.setAttribute("data-date-picked", "1");
    } else {
      // Storefront default: empty until the shopper picks a day.
      input.value = "";
      input.removeAttribute("data-date");
      input.removeAttribute("data-date-picked");
      hasPicked = false;
      if (window.jQuery) window.jQuery(input).removeData("date");
    }

    var pop = document.createElement("div");
    pop.className = "rf-datepicker";
    pop.hidden = true;
    pop.setAttribute("role", "dialog");
    pop.setAttribute("aria-label", "Choose delivery date");
    document.body.appendChild(pop);

    var view = new Date(selected.getFullYear(), selected.getMonth(), 1);

    function currentVendor() {
      return input.__rfDateVendor || vendor;
    }

    function currentMin() {
      var cfg = input.__rfDateConfig || config;
      if (cfg.minDate) return startOfDay(cfg.minDate);
      return skipToVendorDelivery(
        getEarliestArrivalDay(cfg.shipSpeed, currentVendor()),
        currentVendor(),
      );
    }

    function render() {
      var year = view.getFullYear();
      var month = view.getMonth();
      var firstDow = new Date(year, month, 1).getDay();
      var daysInMonth = new Date(year, month + 1, 0).getDate();
      var monthLabel = view.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      var note =
        currentVendor() === "D"
          ? "Wednesday & Thursday delivery only"
          : "Weekday delivery only";
      var html =
        '<div class="rf-datepicker__head">' +
        '<button type="button" class="rf-datepicker__nav" data-dp-prev aria-label="Previous month">‹</button>' +
        '<p class="rf-datepicker__month">' +
        monthLabel +
        "</p>" +
        '<button type="button" class="rf-datepicker__nav" data-dp-next aria-label="Next month">›</button>' +
        "</div>" +
        '<div class="rf-datepicker__dows">' +
        "<span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>" +
        '</div><div class="rf-datepicker__grid">';

      var i;
      var min = currentMin();
      for (i = 0; i < firstDow; i++)
        html += '<span class="rf-datepicker__day is-empty"></span>';
      for (var day = 1; day <= daysInMonth; day++) {
        var date = new Date(year, month, day);
        var allowed = isDeliveryDateAllowed(date, min, currentVendor());
        var isSelected =
          hasPicked && formatISODate(date) === formatISODate(selected);
        html +=
          '<button type="button" class="rf-datepicker__day' +
          (allowed ? "" : " is-disabled") +
          (isSelected ? " is-selected" : "") +
          '" data-dp-day="' +
          day +
          '"' +
          (allowed ? "" : " disabled") +
          ">" +
          day +
          "</button>";
      }
      html +=
        '</div><p class="rf-datepicker__note">' +
        note +
        "</p>" +
        '<p class="rf-datepicker__carrier-note"><strong>Important!</strong> In some instances, your carrier may deliver your roses 1 day early. We will keep you apprised along the way.</p>';
      pop.innerHTML = html;

      var prev = qs("[data-dp-prev]", pop);
      var next = qs("[data-dp-next]", pop);
      if (prev) {
        prev.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          view = new Date(year, month - 1, 1);
          render();
        });
      }
      if (next) {
        next.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          view = new Date(year, month + 1, 1);
          render();
        });
      }
      qsa("[data-dp-day]", pop).forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          var d = new Date(
            year,
            month,
            parseInt(btn.getAttribute("data-dp-day"), 10),
          );
          if (!isDeliveryDateAllowed(d, currentMin(), currentVendor())) return;
          applyDate(d);
          var cfg = input.__rfDateConfig || config;
          if (typeof cfg.datePickCallback === "function") {
            cfg.datePickCallback({
              selectedYear: d.getFullYear(),
              selectedMonth: d.getMonth(),
              selectedDay: d.getDate(),
            });
          }
          close();
        });
      });
    }

    function position() {
      var rect = input.getBoundingClientRect();
      pop.style.position = "fixed";
      pop.style.left =
        Math.max(12, Math.min(rect.left, window.innerWidth - 320)) + "px";
      pop.style.top =
        Math.min(rect.bottom + 8, window.innerHeight - 360) + "px";
      pop.style.zIndex = "2147483646";
    }

    function open() {
      var min = currentMin();
      if (!isDeliveryDateAllowed(selected, min, currentVendor())) {
        selected = min;
        // Don't auto-fill require-pick fields just by opening the calendar.
        if (hasPicked || !requirePick) applyDate(selected, true);
      }
      view = new Date(selected.getFullYear(), selected.getMonth(), 1);
      render();
      position();
      pop.hidden = false;
    }

    function close() {
      pop.hidden = true;
    }

    // Pointer focus must NOT open the calendar. Opening under the cursor lets
    // the same gesture's mouseup/click land on a day button and immediately
    // select+close. Mouse opens on click; keyboard opens on focus.
    var pointerOpening = false;

    input.addEventListener(
      "pointerdown",
      function () {
        pointerOpening = true;
      },
      true,
    );

    input.addEventListener("focus", function () {
      if (pointerOpening) {
        pointerOpening = false;
        return;
      }
      if (pop.hidden) open();
    });

    input.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      pointerOpening = false;
      if (pop.hidden) open();
      else close();
    });

    // Keep month navigation from bubbling to the document closer. render()
    // replaces innerHTML, so a bubbled click's target is no longer inside pop.
    pop.addEventListener("mousedown", function (e) {
      e.stopPropagation();
    });
    pop.addEventListener("click", function (e) {
      e.stopPropagation();
    });

    document.addEventListener(
      "pointerdown",
      function (e) {
        if (pop.hidden) return;
        if (e.target === input || pop.contains(e.target)) return;
        close();
      },
      true,
    );
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") close();
    });
  }
})();
